import { supabase } from './supabaseClient';
import { Profile, Task, Challenge, LedgerEntry, Badge, DayLog, TaskCompletion, RankingUser, LeagueLeaderboardUser, WeeklyLeagueStatus, Friend, FriendRequest, FriendSearchResult, FriendshipStatus, ChallengeTask, ChallengeDetailData, ChallengeEvidenceItem, ChallengeLeaderboardEntry, ChallengeParticipantActivityDay, ChallengeParticipantDayEntry, ChallengeTaskCompletion, ChallengeTaskWithState, FriendDuel, UserSocialSignals, SystemLogEntry, ConsistencyStats, DirectConversationSummary, DirectMessage, AppNotificationSummary, AppNotificationItem, TaskCategory, InviteCodeSummary, AccessGrantSummary, AccessRedeemResult, CampaignCodeCreateResult, ChallengeEvidenceReviewResult, ChallengeJoinResult, ChallengeJoinRequest } from '../types';
import { normalizeUserRole } from './tier-system';
import { CHAT_IMAGES_BUCKET, JOURNAL_IMAGES_BUCKET, TASK_EVIDENCE_BUCKET, getSignedPrivateImageUrl, uploadPrivateImage } from './storage';
import { getLocalDateOnlyFromValue, getTodayLocalDateOnly, toLocalDateOnly } from './date';
import { logError, logInfo, logWarn } from './logger';
import { getBadgesForProfile } from './badges';
import { getChallengeTaskProgress, getChallengeTaskTarget } from './challenge-progress';
import { buildWeeklyStatus, getLeagueTier, getShiftedWeekRange } from './league';
import { didEvidenceDateMismatch, extractImageMetadata } from './image-metadata';
import { triggerPushNotification } from './push-notifications';
import { syncProfileStatsFromActivity } from './stats';

const toDateOnly = (value?: string | null) => {
  if (!value) return null;

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value.slice(0, 10);
  }

  return toLocalDateOnly(parsedDate);
};

const getCurrentDateOnly = () => getTodayLocalDateOnly();
const USER_TASK_FIXED_XP = 50;
const CHALLENGE_TASK_FIXED_XP = 50;
const CHALLENGE_PERFECT_DAY_BONUS_XP = 50;

const isDateInsideRange = (value: string | null | undefined, start: Date, end: Date) => {
  if (!value) return false;
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return false;
  }

  return parsedDate >= start && parsedDate <= end;
};

const getWeekStartDateOnly = (date: Date) => {
  const startOfWeek = new Date(date);
  const dayOfWeek = startOfWeek.getDay();
  const distanceToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  startOfWeek.setHours(0, 0, 0, 0);
  startOfWeek.setDate(startOfWeek.getDate() - distanceToMonday);
  return toLocalDateOnly(startOfWeek);
};

const getMonthStartDateOnly = (date: Date) => {
  const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  return toLocalDateOnly(startOfMonth);
};

const addDays = (date: Date, amount: number) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + amount);
  return nextDate;
};

const getDayDifference = (fromDateOnly: string, toDateOnly: string) => {
  const from = new Date(`${fromDateOnly}T00:00:00`);
  const to = new Date(`${toDateOnly}T00:00:00`);
  const diffMs = to.getTime() - from.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
};

const isActiveDayLog = (status?: string | null) => status === 'active' || status === 'perfect';

const calculateConsistencyStatsFromLogs = (dayLogs: DayLog[]): ConsistencyStats => {
  const activeDates = [...new Set(
    dayLogs
      .filter((log) => isActiveDayLog(log.status))
      .map((log) => log.date)
      .filter(Boolean)
  )].sort();

  if (!activeDates.length) {
    return {
      current_streak: 0,
      best_streak: 0,
      last_active_date: null
    };
  }

  let bestStreak = 1;
  let runningBest = 1;

  for (let index = 1; index < activeDates.length; index += 1) {
    const previousDate = activeDates[index - 1];
    const currentDate = activeDates[index];
    const diff = getDayDifference(previousDate, currentDate);

    if (diff === 1) {
      runningBest += 1;
      bestStreak = Math.max(bestStreak, runningBest);
    } else if (diff > 1) {
      runningBest = 1;
    }
  }

  const lastActiveDate = activeDates[activeDates.length - 1];
  let currentStreak = 0;
  const daysSinceLastActive = getDayDifference(lastActiveDate, getCurrentDateOnly());

  if (daysSinceLastActive <= 1) {
    currentStreak = 1;

    for (let index = activeDates.length - 1; index > 0; index -= 1) {
      const currentDate = activeDates[index];
      const previousDate = activeDates[index - 1];
      const diff = getDayDifference(previousDate, currentDate);

      if (diff === 1) {
        currentStreak += 1;
        continue;
      }

      break;
    }
  }

  return {
    current_streak: currentStreak,
    best_streak: bestStreak,
    last_active_date: lastActiveDate
  };
};

const mergeDayLogsByDate = (dayLogs: DayLog[]): DayLog[] => {
  const mergedLogs = new Map<string, DayLog>();

  dayLogs.forEach((log) => {
    const currentLog = mergedLogs.get(log.date);
    if (!currentLog) {
      mergedLogs.set(log.date, log);
      return;
    }

    // Comentario em pt-BR: se existirem linhas duplicadas para o mesmo dia,
    // a gente preserva a versao mais forte do status e o maior XP do snapshot.
    const mergedStatus: DayLog['status'] =
      currentLog.status === 'perfect' || log.status === 'perfect'
        ? 'perfect'
        : currentLog.status === 'active' || log.status === 'active'
          ? 'active'
          : 'inactive';

    mergedLogs.set(log.date, {
      date: log.date,
      status: mergedStatus,
      xp_earned: Math.max(Number(currentLog.xp_earned || 0), Number(log.xp_earned || 0))
    });
  });

  return Array.from(mergedLogs.values()).sort((a, b) => b.date.localeCompare(a.date));
};

const isRepeatingTask = (task: Pick<Task, 'task_type' | 'frequency'>) => {
  return task.task_type === 'habit' || task.task_type === 'recurring';
};

const isCompletionStillValid = (
  task: Pick<Task, 'task_type' | 'frequency' | 'is_completed' | 'completed_at'>
) => {
  if (!task.is_completed) {
    return false;
  }

  if (!isRepeatingTask(task)) {
    return true;
  }

  const completedDate = toDateOnly(task.completed_at);
  if (!completedDate) {
    return false;
  }

  const now = new Date();
  const today = getCurrentDateOnly();

  if (task.frequency === 'daily') {
    return completedDate === today;
  }

  if (task.frequency === 'weekly') {
    return completedDate >= getWeekStartDateOnly(now);
  }

  if (task.frequency === 'monthly') {
    return completedDate >= getMonthStartDateOnly(now);
  }

  return true;
};

const getChallengeStatus = (challenge: Pick<Challenge, 'starts_at' | 'ends_at' | 'is_perennial'>): Challenge['status'] => {
  const now = new Date();

  if (challenge.starts_at) {
    const startsAt = new Date(challenge.starts_at);
    if (startsAt > now) {
      return 'upcoming';
    }
  }

  if (challenge.is_perennial || !challenge.ends_at) {
    return 'active';
  }

  const endsAt = new Date(challenge.ends_at);
  if (endsAt >= now) {
    return 'active';
  }

  const hideAfter = addDays(endsAt, 7);
  return hideAfter >= now ? 'ended_recently' : 'archived';
};

const buildChallengePrivateAccessToken = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }

  return `${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
};

const buildChallengePrivateAccessCode = () => {
  const randomChunk = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `ARENA-${randomChunk}`;
};

type ChallengeScoreSnapshot = {
  score_xp: number;
  valid_completions: number;
  rejected_completions: number;
  evidences_count: number;
  reported_evidences_count: number;
  pending_review_count: number;
  highest_report_percent: number;
  perfect_days: number;
};

const createEmptyChallengeScoreSnapshot = (): ChallengeScoreSnapshot => ({
  score_xp: 0,
  valid_completions: 0,
  rejected_completions: 0,
  evidences_count: 0,
  reported_evidences_count: 0,
  pending_review_count: 0,
  highest_report_percent: 0,
  perfect_days: 0
});

const estimateChallengeValidCompletionsFromProgress = (progressPercent: number, totalExpectedCompletions: number) => {
  if (progressPercent <= 0 || totalExpectedCompletions <= 0) {
    return 0;
  }

  const estimatedCompletions = Math.round((progressPercent / 100) * totalExpectedCompletions);
  return Math.min(totalExpectedCompletions, Math.max(0, estimatedCompletions));
};

const normalizeChallengeLeaderboardEntries = (
  entries: ChallengeLeaderboardEntry[],
  totalExpectedCompletions: number
) => entries.map((entry) => {
  const fallbackValidCompletions = entry.valid_completions > 0
    ? entry.valid_completions
    : estimateChallengeValidCompletionsFromProgress(entry.progress_percent, totalExpectedCompletions);

  const fallbackScoreXp = entry.score_xp > 0
    ? entry.score_xp
    : fallbackValidCompletions > 0
      ? fallbackValidCompletions * CHALLENGE_TASK_FIXED_XP
      : 0;

  return {
    ...entry,
    valid_completions: fallbackValidCompletions,
    score_xp: fallbackScoreXp
  };
});

const sortChallengeLeaderboardEntries = (entries: ChallengeLeaderboardEntry[]) => (
  [...entries]
    .sort((a, b) => {
      if (b.score_xp !== a.score_xp) return b.score_xp - a.score_xp;
      if ((b.perfect_days || 0) !== (a.perfect_days || 0)) return (b.perfect_days || 0) - (a.perfect_days || 0);
      if (b.valid_completions !== a.valid_completions) return b.valid_completions - a.valid_completions;
      return a.display_name.localeCompare(b.display_name);
    })
    .map((entry, index) => ({ ...entry, rank: index + 1 }))
);

type ChallengeParticipantCompletionFeedRow = ChallengeTaskCompletion & {
  task_title?: string | null;
  task_description?: string | null;
  task_requires_evidence?: boolean | null;
  task_xp_reward?: number | null;
};

const normalizeChallengeTaskKey = (taskId?: string | null, taskTitle?: string | null) => {
  if (taskId) {
    return taskId;
  }

  if (taskTitle) {
    return taskTitle.trim().toLowerCase();
  }

  return null;
};

const getChallengeParticipantCompletionFeed = async (
  challengeId: string,
  participantId: string
): Promise<ChallengeParticipantCompletionFeedRow[]> => {
  const loadFallbackRows = async (): Promise<ChallengeParticipantCompletionFeedRow[]> => {
    const { data: fallbackRows, error: fallbackError } = await supabase
      .from('challenge_task_completions')
      .select(`
        *,
        challenge_tasks:challenge_task_id (
          title,
          description,
          xp_reward,
          requires_evidence
        )
      `)
      .eq('participant_id', participantId)
      .order('completed_at', { ascending: false });

    if (fallbackError) {
      logError('challenge-detail', 'Falha ao buscar o feed do participante no challenge.', {
        challengeId,
        participantId,
        error: fallbackError
      });
      return [];
    }

    return ((fallbackRows as any[]) || []).map((row) => ({
      ...(row as ChallengeTaskCompletion),
      task_title: row.challenge_tasks?.title || null,
      task_description: row.challenge_tasks?.description || null,
      task_requires_evidence: Boolean(row.challenge_tasks?.requires_evidence),
      task_xp_reward: Number(row.challenge_tasks?.xp_reward || CHALLENGE_TASK_FIXED_XP)
    }));
  };

  const { data, error } = await supabase
    .rpc('get_challenge_participant_completion_feed', {
      p_challenge_id: challengeId,
      p_participant_id: participantId
    });

  if (!error) {
    const rpcRows = ((data as any[]) || []).map((row) => ({
      id: row.id,
      participant_id: row.participant_id,
      challenge_task_id: row.challenge_task_id,
      completed_at: row.completed_at,
      completion_date: row.completion_date || undefined,
      evidence_url: row.evidence_url || null,
      evidence_captured_at: row.evidence_captured_at || null,
      evidence_storage_path: row.evidence_storage_path || null,
      evidence_status: row.evidence_status || 'approved',
      moderation_reason: row.moderation_reason || null,
      moderated_at: row.moderated_at || null,
      moderated_by_user_id: row.moderated_by_user_id || null,
      report_reviewed_at: row.report_reviewed_at || null,
      report_reviewed_by_user_id: row.report_reviewed_by_user_id || null,
      report_review_note: row.report_review_note || null,
      task_title: row.task_title || null,
      task_description: row.task_description || null,
      task_requires_evidence: Boolean(row.task_requires_evidence),
      task_xp_reward: Number(row.task_xp_reward || CHALLENGE_TASK_FIXED_XP)
    }));

    const needsEnrichment = rpcRows.some((row) =>
      (Boolean(row.task_requires_evidence) && !row.evidence_storage_path && !row.evidence_url)
      || !row.task_title
    );

    if (!needsEnrichment) {
      return rpcRows;
    }

    const fallbackRows = await loadFallbackRows();
    const fallbackById = new Map(fallbackRows.map((row) => [row.id, row]));

    return rpcRows.map((row) => {
      const fallbackRow = fallbackById.get(row.id);
      if (!fallbackRow) {
        return row;
      }

      return {
        ...fallbackRow,
        ...row,
        evidence_url: row.evidence_url || fallbackRow.evidence_url || null,
        evidence_storage_path: row.evidence_storage_path || fallbackRow.evidence_storage_path || null,
        task_title: row.task_title || fallbackRow.task_title || null,
        task_description: row.task_description || fallbackRow.task_description || null,
        task_requires_evidence: Boolean(row.task_requires_evidence || fallbackRow.task_requires_evidence),
        task_xp_reward: Number(row.task_xp_reward || fallbackRow.task_xp_reward || CHALLENGE_TASK_FIXED_XP)
      };
    });
  }

  logWarn('challenge-detail', 'RPC do feed do participante no challenge indisponivel. Tentando leitura direta.', {
    challengeId,
    participantId,
    error
  });

  return loadFallbackRows();
};

const buildChallengeScoreSnapshots = (
  participantRows: any[],
  completionRows: any[],
  taskRows: any[],
  reportRows: any[]
) => {
  // Comentario em pt-BR: o score do challenge agora e fixo,
  // entao a gente soma 50 XP por tarefa valida e mais 50 XP quando fecha o dia perfeito.
  const eligibleReviewers = Math.max(1, participantRows.length - 1);
  const reportCountByCompletion = new Map<string, number>();
  const dailyTaskIds = new Set(
    taskRows
      .filter((task) => task.frequency === 'daily')
      .map((task) => task.id)
      .filter(Boolean)
  );
  const dailyTaskCount = dailyTaskIds.size;
  const statsByParticipant = new Map<string, ChallengeScoreSnapshot>();
  const dailyCoverageByParticipant = new Map<string, Map<string, Set<string>>>();

  (reportRows || []).forEach((report) => {
    reportCountByCompletion.set(report.completion_id, (reportCountByCompletion.get(report.completion_id) || 0) + 1);
  });

  (completionRows || []).forEach((completion) => {
    const participantId = completion.participant_id;
    const current = statsByParticipant.get(participantId) || createEmptyChallengeScoreSnapshot();
    const reportCount = reportCountByCompletion.get(completion.id) || 0;
    const reportPercent = reportCount > 0 ? Math.round((reportCount / eligibleReviewers) * 100) : 0;

    if (completion.evidence_storage_path) {
      current.evidences_count += 1;
    }

    if (reportCount > 0) {
      current.reported_evidences_count += 1;
      current.highest_report_percent = Math.max(current.highest_report_percent, reportPercent);

      if (!completion.report_reviewed_at && completion.evidence_status !== 'rejected') {
        current.pending_review_count += 1;
      }
    }

    if (completion.evidence_status === 'rejected') {
      current.rejected_completions += 1;
      statsByParticipant.set(participantId, current);
      return;
    }

    current.valid_completions += 1;
    current.score_xp += CHALLENGE_TASK_FIXED_XP;

    if (dailyTaskCount > 0 && dailyTaskIds.has(completion.challenge_task_id)) {
      const completionDate = getLocalDateOnlyFromValue(completion.completed_at) || completion.completion_date || null;
      if (completionDate) {
        const participantDays = dailyCoverageByParticipant.get(participantId) || new Map<string, Set<string>>();
        const completedTaskIds = participantDays.get(completionDate) || new Set<string>();
        completedTaskIds.add(completion.challenge_task_id);
        participantDays.set(completionDate, completedTaskIds);
        dailyCoverageByParticipant.set(participantId, participantDays);
      }
    }

    statsByParticipant.set(participantId, current);
  });

  if (dailyTaskCount > 0) {
    dailyCoverageByParticipant.forEach((participantDays, participantId) => {
      const current = statsByParticipant.get(participantId) || createEmptyChallengeScoreSnapshot();

      participantDays.forEach((completedTaskIds) => {
        if (completedTaskIds.size >= dailyTaskCount) {
          current.score_xp += CHALLENGE_PERFECT_DAY_BONUS_XP;
          current.perfect_days += 1;
        }
      });

      statsByParticipant.set(participantId, current);
    });
  }

  return statsByParticipant;
};

// Profile operations
const getSafeNumber = (value: unknown) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const normalizeProfileStats = (rawStats: unknown) => {
  const fallbackStats = { str: 0, int: 0, foc: 0, spi: 0, cha: 0 };

  if (!rawStats) {
    return fallbackStats;
  }

  const resolvedStats = typeof rawStats === 'string'
    ? (() => {
        try {
          return JSON.parse(rawStats);
        } catch (error) {
          logWarn('profile', 'Nao deu para ler o JSON de atributos do perfil.', { error, rawStats });
          return null;
        }
      })()
    : rawStats;

  if (!resolvedStats || typeof resolvedStats !== 'object') {
    return fallbackStats;
  }

  const statsRow = resolvedStats as Record<string, unknown>;
  return {
    str: getSafeNumber(statsRow.str),
    int: getSafeNumber(statsRow.int),
    foc: getSafeNumber(statsRow.foc),
    spi: getSafeNumber(statsRow.spi),
    cha: getSafeNumber(statsRow.cha)
  };
};

const mapProfileRow = (row: any): Profile => ({
  ...row,
  total_xp: getSafeNumber(row?.total_xp),
  aura_level: getSafeNumber(row?.aura_level),
  streak: getSafeNumber(row?.streak),
  credits: getSafeNumber(row?.credits),
  roulette_tickets: getSafeNumber(row?.roulette_tickets),
  power_hour_combo: getSafeNumber(row?.power_hour_combo),
  stats: normalizeProfileStats(row?.stats)
});

export const getProfile = async (userId: string): Promise<Profile | null> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('Error fetching profile:', error);
    return null;
  }

  const mappedProfile = mapProfileRow(data);
  const syncedStats = await syncProfileStatsFromActivity(userId, {
    currentStats: mappedProfile.stats
  });

  return {
    ...mappedProfile,
    stats: syncedStats
  };
};

export const getPublicProfile = async (userId: string): Promise<Profile | null> => {
  const { data, error } = await supabase.rpc('get_public_profile_snapshot', {
    p_user_id: userId
  });

  if (error) {
    logWarn('profile', 'Falha ao buscar snapshot publico do perfil. Vou tentar a leitura padrao.', error);
    return getProfile(userId);
  }

  const profileRow = Array.isArray(data) ? data[0] : data;
  if (!profileRow) {
    return null;
  }

  return mapProfileRow(profileRow);
};

export const reconcileCurrentUserAccessState = async (): Promise<boolean> => {
  const { error } = await supabase.rpc('reconcile_profile_access');

  if (error) {
    logError('access', 'Falha ao reconciliar acesso atual do usuario.', error);
    return false;
  }

  return true;
};

const mapInviteCodeSummary = (row: any): InviteCodeSummary => ({
  id: row.id,
  code: row.code,
  code_kind: row.code_kind,
  campaign_name: row.campaign_name || null,
  trial_days: Number(row.trial_days || 0),
  max_redemptions: Number(row.max_redemptions || 1),
  active_until: row.active_until || null,
  is_active: Boolean(row.is_active),
  created_at: row.created_at,
  owner_user_id: row.owner_user_id || null,
  redeemed_user_id: row.redeemed_user_id || null,
  redeemed_display_name: row.redeemed_display_name || null,
  redeemed_username: row.redeemed_username || null,
  redeemed_at: row.redeemed_at || null,
  ends_at: row.ends_at || null,
  redemption_count: Number(row.redemption_count || 0)
});

export const getOrCreateProInviteCodes = async (): Promise<InviteCodeSummary[]> => {
  const { data, error } = await supabase.rpc('get_or_create_pro_invite_codes');

  if (error) {
    logError('access', 'Falha ao carregar ou criar convites do Pro pago.', error);
    return [];
  }

  return ((data as any[]) || []).map((row) => ({
    id: row.id,
    code: row.code,
    code_kind: 'pro_invite',
    trial_days: Number(row.trial_days || 30),
    max_redemptions: Number(row.max_redemptions || 1),
    is_active: true,
    created_at: row.created_at,
    redeemed_user_id: row.redeemed_user_id || null,
    redeemed_display_name: row.redeemed_display_name || null,
    redeemed_username: row.redeemed_username || null,
    redeemed_at: row.redeemed_at || null,
    ends_at: row.ends_at || null
  }));
};

export const redeemAccessCode = async (code: string): Promise<AccessRedeemResult> => {
  const { data, error } = await supabase.rpc('redeem_access_code', {
    p_code: code.trim()
  });

  if (error) {
    logError('access', 'Falha ao resgatar codigo de acesso.', error);
    return {
      ok: false,
      message: 'Não foi possível validar esse código agora.'
    };
  }

  return (data as AccessRedeemResult) || {
    ok: false,
    message: 'Codigo sem resposta valida.'
  };
};

export const getMyAccessGrant = async (userId: string): Promise<AccessGrantSummary | null> => {
  const { data, error } = await supabase
    .from('access_grants')
    .select(`
      id,
      user_id,
      invite_code_id,
      granted_role,
      source_type,
      campaign_name,
      inviter_user_id,
      status,
      starts_at,
      ends_at,
      redeemed_at,
      invite_codes:invite_code_id(code),
      inviter:inviter_user_id(display_name, username)
    `)
    .eq('user_id', userId)
    .in('status', ['active', 'expired', 'converted_paid'])
    .order('redeemed_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    logError('access', 'Falha ao buscar grant de acesso do usuario.', error);
    return null;
  }

  if (!data) {
    return null;
  }

  const row = data as any;
  return {
    id: row.id,
    user_id: row.user_id,
    invite_code_id: row.invite_code_id,
    granted_role: row.granted_role,
    source_type: row.source_type,
    campaign_name: row.campaign_name || null,
    inviter_user_id: row.inviter_user_id || null,
    status: row.status,
    starts_at: row.starts_at,
    ends_at: row.ends_at,
    redeemed_at: row.redeemed_at,
    inviter_display_name: row.inviter?.display_name || null,
    inviter_username: row.inviter?.username || null,
    invite_code: row.invite_codes?.code || null
  };
};

export const getLeaderboard = async (limit: number = 50): Promise<RankingUser[]> => {
  // Comentario em pt-BR: leaderboard deve mostrar quem realmente pontuou.
  // Isso evita cards com 0 XP que poluem a lista.
  const { data, error } = await supabase
    .from('profiles')
    .select('id, username, display_name, avatar_url, aura_level, total_xp, streak, user_role')
    .gt('total_xp', 0)
    .order('total_xp', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching leaderboard:', error);
    return [];
  }

  const rows = (data as any[]) || [];
  const profileIds = rows.map((row) => row.id);
  const badgeCounts = new Map<string, number>();
  const socialSignals = await getUserSocialSignals(profileIds);

  if (profileIds.length) {
    const { data: badgesData, error: badgesError } = await supabase
      .from('badges')
      .select('user_id')
      .in('user_id', profileIds);

    if (badgesError) {
      console.error('Error fetching badge counts for leaderboard:', badgesError);
    } else {
      ((badgesData as Array<{ user_id: string }>) || []).forEach((badge) => {
        badgeCounts.set(badge.user_id, (badgeCounts.get(badge.user_id) || 0) + 1);
      });
    }
  }

  return rows.map((row, index) => {
    // Comentario em pt-BR: normalizamos para numero porque dependendo do ambiente
    // alguns campos podem vir como string e isso baguncava exibicao de XP.
    const auraLevel = Number(row.aura_level ?? 1);
    const totalXp = Number(row.total_xp ?? 0);
    const streak = Number(row.streak ?? 0);

    return {
      id: row.id,
      username: row.username || row.display_name || `player_${index + 1}`,
      display_name: row.display_name || undefined,
      avatar_url: row.avatar_url || undefined,
      aura_level: Number.isFinite(auraLevel) ? auraLevel : 1,
      total_xp: Number.isFinite(totalXp) ? totalXp : 0,
      user_role: row.user_role || 'free',
      rank: index + 1,
      badges: badgeCounts.get(row.id) || 0,
      streak: Number.isFinite(streak) ? streak : 0,
      friend_count: socialSignals.get(row.id)?.friend_count || 0,
      duel_wins: socialSignals.get(row.id)?.duel_wins || 0,
      active_duels: socialSignals.get(row.id)?.active_duels || 0
    };
  });
};

const getWeeklyXpByUser = async (
  userIds: string[],
  weekOffset: number,
  now: Date = new Date()
): Promise<Map<string, number>> => {
  const xpByUser = new Map<string, number>();

  if (!userIds.length) {
    return xpByUser;
  }

  const { data: snapshotRows, error: snapshotError } = await supabase.rpc('get_weekly_xp_snapshot', {
    p_user_ids: userIds,
    p_week_offset: weekOffset
  });

  if (!snapshotError && Array.isArray(snapshotRows)) {
    ((snapshotRows as Array<{ user_id: string; weekly_xp: number }>)).forEach((row) => {
      xpByUser.set(row.user_id, Number(row.weekly_xp || 0));
    });

    logInfo('ranking', 'XP semanal carregado pelo RPC oficial.', {
      weekOffset,
      totalUsers: userIds.length,
      rows: snapshotRows.length
    });

    return xpByUser;
  }

  logWarn('ranking', 'RPC oficial do XP semanal indisponivel. Voltando para fallback local.', {
    weekOffset,
    error: snapshotError
  });

  const weekRange = getShiftedWeekRange(weekOffset, now);
  await syncPowerHourXpHistoryForWeek(userIds, weekRange.start, weekRange.end);
  const { data, error } = await supabase
    .from('xp_history')
    .select('user_id, amount, date')
    .in('user_id', userIds)
    .gte('date', weekRange.start.toISOString())
    .lte('date', weekRange.end.toISOString());

  if (error) {
    logError('ranking', 'Falha ao buscar XP semanal para o ranking.', { weekOffset, error });
    return xpByUser;
  }

  ((data as Array<{ user_id: string; amount: number }>) || []).forEach((entry) => {
    xpByUser.set(entry.user_id, (xpByUser.get(entry.user_id) || 0) + Number(entry.amount || 0));
  });

  return xpByUser;
};

const getRecoveredWeeklyXpByUser = async (
  userIds: string[],
  weekOffset: number,
  now: Date = new Date()
): Promise<Map<string, number>> => {
  const recoveredXp = new Map<string, number>();

  if (!userIds.length) {
    return recoveredXp;
  }

  const weekRange = getShiftedWeekRange(weekOffset, now);

  const [{ data: taskCompletions, error: taskCompletionsError }, { data: focusSessions, error: focusSessionsError }] = await Promise.all([
    supabase
      .from('task_completions')
      .select('user_id, xp_earned, completed_at')
      .in('user_id', userIds)
      .gte('completed_at', weekRange.start.toISOString())
      .lte('completed_at', weekRange.end.toISOString()),
    supabase
      .from('focus_sessions')
      .select('user_id, xp_earned, completed_at')
      .in('user_id', userIds)
      .gte('completed_at', weekRange.start.toISOString())
      .lte('completed_at', weekRange.end.toISOString())
  ]);

  if (taskCompletionsError) {
    logError('ranking', 'Falha ao recuperar XP semanal a partir de task_completions.', {
      weekOffset,
      error: taskCompletionsError
    });
  }

  if (focusSessionsError) {
    logError('ranking', 'Falha ao recuperar XP semanal a partir de focus_sessions.', {
      weekOffset,
      error: focusSessionsError
    });
  }

  ((taskCompletions as Array<{ user_id: string; xp_earned: number }>) || []).forEach((completion) => {
    recoveredXp.set(
      completion.user_id,
      (recoveredXp.get(completion.user_id) || 0) + Number(completion.xp_earned || 0)
    );
  });

  ((focusSessions as Array<{ user_id: string; xp_earned: number }>) || []).forEach((session) => {
    recoveredXp.set(
      session.user_id,
      (recoveredXp.get(session.user_id) || 0) + Number(session.xp_earned || 0)
    );
  });

  logInfo('ranking', 'Recuperacao auxiliar do XP semanal calculada.', {
    weekOffset,
    totalUsers: userIds.length,
    recoveredUsers: recoveredXp.size
  });

  return recoveredXp;
};

const resolveWeeklyLeaguePoints = (input: {
  rawWeeklyXp: number;
  previousWeekXp: number;
  totalXp: number;
  createdAt?: string | null;
  currentWeekStart: Date;
  currentWeekEnd: Date;
}) => {
  const { rawWeeklyXp, previousWeekXp, totalXp, createdAt, currentWeekStart, currentWeekEnd } = input;

  if (rawWeeklyXp > 0) {
    return rawWeeklyXp;
  }

  // Comentario em pt-BR: conta nova criada nesta semana pode ter XP inicial ou ganho antigo
  // que ainda nao caiu no xp_history. Nesses casos, usamos o XP total como fallback
  // para a primeira semana do player nao ficar zerada no ranking.
  if (
    totalXp > 0 &&
    previousWeekXp <= 0 &&
    isDateInsideRange(createdAt, currentWeekStart, currentWeekEnd)
  ) {
    return totalXp;
  }

  return rawWeeklyXp;
};

const buildPowerHourXpSource = (sessionId: string, durationMinutes: number) =>
  `Power Hour (${durationMinutes} min) [session:${sessionId}]`;

const syncPowerHourXpHistoryForWeek = async (
  userIds: string[],
  start: Date,
  end: Date
): Promise<void> => {
  if (!userIds.length) {
    return;
  }

  const { data: powerSessions, error: powerSessionsError } = await supabase
    .from('power_sessions')
    .select('id, user_id, duration_minutes, xp_earned, created_at, completed')
    .in('user_id', userIds)
    .eq('completed', true)
    .gte('created_at', start.toISOString())
    .lte('created_at', end.toISOString());

  if (powerSessionsError) {
    logError('ranking', 'Falha ao sincronizar power sessions no historico semanal.', powerSessionsError);
    return;
  }

  const sessions = ((powerSessions as Array<{
    id: string;
    user_id: string;
    duration_minutes: number;
    xp_earned: number;
    created_at: string;
  }>) || []).filter((session) => session.id && Number(session.xp_earned || 0) !== 0);

  if (!sessions.length) {
    return;
  }

  const powerSources = sessions.map((session) => buildPowerHourXpSource(session.id, Number(session.duration_minutes || 0)));
  const { data: existingHistory, error: historyError } = await supabase
    .from('xp_history')
    .select('source')
    .in('source', powerSources);

  if (historyError) {
    logError('ranking', 'Falha ao conferir power sessions ja sincronizadas no XP history.', historyError);
    return;
  }

  const existingSources = new Set((((existingHistory as Array<{ source: string }>) || [])).map((entry) => entry.source));
  const missingEntries = sessions
    .filter((session) => !existingSources.has(buildPowerHourXpSource(session.id, Number(session.duration_minutes || 0))))
    .map((session) => ({
      user_id: session.user_id,
      source: buildPowerHourXpSource(session.id, Number(session.duration_minutes || 0)),
      amount: Number(session.xp_earned || 0),
      date: session.created_at
    }));

  if (!missingEntries.length) {
    return;
  }

  const { error: insertError } = await supabase
    .from('xp_history')
    .insert(missingEntries);

  if (insertError) {
    logError('ranking', 'Falha ao completar XP semanal faltante de power sessions.', {
      insertError,
      totalMissingEntries: missingEntries.length
    });
    return;
  }

  logInfo('ranking', 'Power sessions antigas foram reconciliadas no XP history.', {
    totalInserted: missingEntries.length,
    userIds
  });
};

export const getWeeklyLeagueStatus = async (userId: string): Promise<WeeklyLeagueStatus> => {
  const currentWeekRange = getShiftedWeekRange(0);
  const [profileRow, currentWeekXpMap, previousWeekXpMap, recoveredCurrentWeekXpMap] = await Promise.all([
    supabase
      .from('profiles')
      .select('total_xp, created_at')
      .eq('id', userId)
      .maybeSingle(),
    getWeeklyXpByUser([userId], 0),
    getWeeklyXpByUser([userId], -1),
    getRecoveredWeeklyXpByUser([userId], 0)
  ]);

  const rawCurrentWeekXp = currentWeekXpMap.get(userId) || 0;
  const recoveredCurrentWeekXp = recoveredCurrentWeekXpMap.get(userId) || 0;
  const resolvedCurrentWeekXp = resolveWeeklyLeaguePoints({
    rawWeeklyXp: rawCurrentWeekXp > 0 ? rawCurrentWeekXp : recoveredCurrentWeekXp,
    previousWeekXp: previousWeekXpMap.get(userId) || 0,
    totalXp: Number(profileRow.data?.total_xp || 0),
    createdAt: profileRow.data?.created_at,
    currentWeekStart: currentWeekRange.start,
    currentWeekEnd: currentWeekRange.end
  });

  return buildWeeklyStatus(
    resolvedCurrentWeekXp,
    previousWeekXpMap.get(userId) || 0
  );
};

export const getWeeklyLeaderboard = async (input: {
  scope: 'global' | 'friends' | 'league';
  currentUserId: string;
  friendIds?: string[];
  limit?: number;
}): Promise<LeagueLeaderboardUser[]> => {
  const { scope, currentUserId, friendIds = [], limit = 50 } = input;

  let profileQuery = supabase
    .from('profiles')
    .select('id, username, display_name, avatar_url, aura_level, total_xp, streak, user_role, created_at');

  if (scope === 'friends') {
    const scopedIds = Array.from(new Set([currentUserId, ...friendIds]));
    profileQuery = profileQuery.in('id', scopedIds);
  }

  const { data, error } = await profileQuery;

  if (error) {
    logError('ranking', 'Falha ao buscar perfis para o ranking semanal.', { scope, error });
    return [];
  }

  const profileRows = (data as any[]) || [];
  const profileIds = profileRows.map((row) => row.id);
  const [currentWeekXpMap, previousWeekXpMap, recoveredCurrentWeekXpMap, socialSignals] = await Promise.all([
    getWeeklyXpByUser(profileIds, 0),
    getWeeklyXpByUser(profileIds, -1),
    getRecoveredWeeklyXpByUser(profileIds, 0),
    getUserSocialSignals(profileIds)
  ]);

  const badgeCounts = new Map<string, number>();
  if (profileIds.length) {
    const { data: badgesData, error: badgesError } = await supabase
      .from('badges')
      .select('user_id')
      .in('user_id', profileIds);

    if (badgesError) {
      logError('ranking', 'Falha ao buscar badges para o ranking semanal.', badgesError);
    } else {
      ((badgesData as Array<{ user_id: string }>) || []).forEach((badge) => {
        badgeCounts.set(badge.user_id, (badgeCounts.get(badge.user_id) || 0) + 1);
      });
    }
  }

  const currentUserPreviousWeekXp = previousWeekXpMap.get(currentUserId) || 0;
  const currentUserLeagueTier = getLeagueTier(currentUserPreviousWeekXp);
  const currentWeekRange = getShiftedWeekRange(0);

  const mappedRows = profileRows.map((row) => {
    const totalXp = Number(row.total_xp ?? 0);
    const previousWeekXp = previousWeekXpMap.get(row.id) || 0;
    const rawWeeklyXp = currentWeekXpMap.get(row.id) || 0;
    const recoveredWeeklyXp = recoveredCurrentWeekXpMap.get(row.id) || 0;
    const weeklyXp = resolveWeeklyLeaguePoints({
      rawWeeklyXp: rawWeeklyXp > 0 ? rawWeeklyXp : recoveredWeeklyXp,
      previousWeekXp,
      totalXp,
      createdAt: row.created_at,
      currentWeekStart: currentWeekRange.start,
      currentWeekEnd: currentWeekRange.end
    });
    const social = socialSignals.get(row.id);
    const leagueTier = getLeagueTier(previousWeekXp);

    return {
      id: row.id,
      username: row.username || 'player',
      display_name: row.display_name || row.username || 'Player',
      avatar_url: row.avatar_url || undefined,
      aura_level: Number(row.aura_level ?? 1),
      total_xp: totalXp,
      weekly_xp: weeklyXp,
      user_role: row.user_role || 'free',
      rank: 0,
      badges: badgeCounts.get(row.id) || 0,
      streak: Number(row.streak ?? 0),
      friend_count: social?.friend_count || 0,
      duel_wins: social?.duel_wins || 0,
      active_duels: social?.active_duels || 0,
      league_tier_id: leagueTier.id
    } satisfies LeagueLeaderboardUser;
  });

  const filteredRows = mappedRows
    .filter((row) => {
      if (scope === 'league') {
        return row.league_tier_id === currentUserLeagueTier.id;
      }

      return true;
    })
    .sort((a, b) => {
      if (b.weekly_xp !== a.weekly_xp) return b.weekly_xp - a.weekly_xp;
      if (b.total_xp !== a.total_xp) return b.total_xp - a.total_xp;
      return a.display_name.localeCompare(b.display_name);
    })
    .slice(0, limit)
    .map((row, index) => ({ ...row, rank: index + 1 }));

  logInfo('ranking', 'Ranking semanal carregado.', {
    scope,
    currentUserId,
    totalRows: filteredRows.length,
    leagueTierId: currentUserLeagueTier.id
  });

  return filteredRows;
};

const buildFriendFromProfile = (profile: Partial<Profile>): Friend => ({
  id: profile.id || '',
  username: profile.username || '',
  display_name: profile.display_name || profile.username || '',
  avatar_url: profile.avatar_url || undefined,
  status: 'offline',
  aura_level: profile.aura_level || 1,
  user_role: profile.user_role,
  total_xp: profile.total_xp || 0,
  streak: profile.streak || 0
});

const isAdminFriendship = (
  currentProfile: Pick<Profile, 'user_role' | 'is_pro'> | null | undefined,
  targetProfile: Pick<Profile, 'user_role' | 'is_pro'> | null | undefined
) => {
  return normalizeUserRole(currentProfile) === 'admin' || normalizeUserRole(targetProfile) === 'admin';
};

type FriendDuelRow = {
  id: string;
  challenger_id: string;
  opponent_id: string;
  winner_id?: string | null;
  title: string;
  description?: string | null;
  stake_credits: number;
  settled_credits: number;
  duration_days: number;
  status: FriendDuel['status'];
  starts_at?: string | null;
  ends_at: string;
  accepted_at?: string | null;
  resolved_at?: string | null;
  resolution_notes?: string | null;
  created_at: string;
  updated_at: string;
};

const mapFriendDuelRow = (
  row: FriendDuelRow,
  profilesMap: Map<string, Friend>,
  currentUserId: string
): FriendDuel => {
  const challenger = profilesMap.get(row.challenger_id) || buildFriendFromProfile({ id: row.challenger_id, username: 'desconhecido' });
  const opponent = profilesMap.get(row.opponent_id) || buildFriendFromProfile({ id: row.opponent_id, username: 'desconhecido' });

  return {
    ...row,
    challenger,
    opponent,
    is_creator: row.challenger_id === currentUserId,
    is_waiting_response: row.status === 'pending' && row.challenger_id === currentUserId,
    can_accept: row.status === 'pending' && row.opponent_id === currentUserId,
    can_resolve: row.status === 'active' && (row.challenger_id === currentUserId || row.opponent_id === currentUserId),
    user_is_winner: row.winner_id === currentUserId
  };
};

export const getFriendshipStatus = async (currentUserId: string, otherUserId: string): Promise<FriendshipStatus> => {
  if (currentUserId === otherUserId) {
    return 'self';
  }

  const [currentProfile, otherProfile] = await Promise.all([
    getProfile(currentUserId),
    getProfile(otherUserId)
  ]);

  if (isAdminFriendship(currentProfile, otherProfile)) {
    return 'friends';
  }

  const { data, error } = await supabase
    .from('friend_requests')
    .select('*')
    .or(`and(requester_id.eq.${currentUserId},receiver_id.eq.${otherUserId}),and(requester_id.eq.${otherUserId},receiver_id.eq.${currentUserId})`)
    .order('created_at', { ascending: false })
    .limit(1);

  if (error || !data?.length) {
    return 'not_friends';
  }

  const latestRequest = data[0] as FriendRequest;
  if (latestRequest.status === 'accepted') {
    return 'friends';
  }

  if (latestRequest.status === 'pending') {
    return latestRequest.requester_id === currentUserId ? 'outgoing_pending' : 'incoming_pending';
  }

  return 'not_friends';
};

export const searchUsersForFriends = async (currentUserId: string, query: string, limit: number = 20): Promise<FriendSearchResult[]> => {
  const searchTerm = query.trim();
  if (!searchTerm) {
    return [];
  }

  const { data, error } = await supabase
    .from('profiles')
    .select('id, username, display_name, avatar_url, aura_level, total_xp, streak, user_role')
    .neq('id', currentUserId)
    .or(`username.ilike.%${searchTerm}%,display_name.ilike.%${searchTerm}%`)
    .order('total_xp', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error searching users for friends:', error);
    return [];
  }

  const profiles = (data as Partial<Profile>[]) || [];
  const statuses = await Promise.all(
    profiles.map(async (profile) => ({
      profile,
      friendship_status: await getFriendshipStatus(currentUserId, profile.id || '')
    }))
  );

  return statuses.map(({ profile, friendship_status }) => ({
    ...buildFriendFromProfile(profile),
    friendship_status
  }));
};

export const getFriendRequestsForUser = async (userId: string): Promise<FriendRequest[]> => {
  const { data, error } = await supabase
    .from('friend_requests')
    .select('*')
    .or(`requester_id.eq.${userId},receiver_id.eq.${userId}`)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching friend requests:', error);
    return [];
  }

  return (data as FriendRequest[]) || [];
};

export const sendFriendRequest = async (requesterId: string, receiverId: string): Promise<boolean> => {
  if (requesterId === receiverId) {
    return false;
  }

  const [requesterProfile, receiverProfile] = await Promise.all([
    getProfile(requesterId),
    getProfile(receiverId)
  ]);

  if (isAdminFriendship(requesterProfile, receiverProfile)) {
    return true;
  }

  const { data: reversePending } = await supabase
    .from('friend_requests')
    .select('id, status')
    .eq('requester_id', receiverId)
    .eq('receiver_id', requesterId)
    .eq('status', 'pending')
    .maybeSingle();

  if (reversePending?.id) {
    const { error: acceptError } = await supabase
      .from('friend_requests')
      .update({
        status: 'accepted',
        responded_at: new Date().toISOString()
      })
      .eq('id', reversePending.id);

    if (!acceptError && requesterProfile) {
      await triggerPushNotification({
        userIds: [receiverId],
        title: 'Novo aliado no Squad',
        message: `${requesterProfile.display_name || requesterProfile.username} aceitou sua solicitacao.`,
        url: '/#/app/friends',
        tag: `friend-accepted:${receiverId}`,
        kind: 'friend_request',
        metadata: {
          requester_id: requesterId
        }
      });
    }

    return !acceptError;
  }

  const { data: existing } = await supabase
    .from('friend_requests')
    .select('id')
    .eq('requester_id', requesterId)
    .eq('receiver_id', receiverId)
    .maybeSingle();

  if (existing?.id) {
    const { error: updateError } = await supabase
      .from('friend_requests')
      .update({
        status: 'pending',
        responded_at: null
      })
      .eq('id', existing.id);

    return !updateError;
  }

  const { error } = await supabase
    .from('friend_requests')
    .insert({
      requester_id: requesterId,
      receiver_id: receiverId,
      status: 'pending'
    });

  if (!error && requesterProfile) {
    await triggerPushNotification({
      userIds: [receiverId],
      title: 'Nova solicitacao de amizade',
      message: `${requesterProfile.display_name || requesterProfile.username} quer entrar no seu Squad.`,
      url: '/#/app/friends',
      tag: `friend-request:${receiverId}`,
      kind: 'friend_request',
      metadata: {
        requester_id: requesterId
      }
    });
  }

  return !error;
};

export const respondToFriendRequest = async (
  requestId: string,
  action: 'accepted' | 'rejected' | 'cancelled'
): Promise<boolean> => {
  const { error } = await supabase
    .from('friend_requests')
    .update({
      status: action,
      responded_at: new Date().toISOString()
    })
    .eq('id', requestId);

  return !error;
};

export const cancelFriendRequest = async (requestId: string): Promise<boolean> => {
  return respondToFriendRequest(requestId, 'cancelled');
};

export const removeFriendship = async (currentUserId: string, otherUserId: string): Promise<boolean> => {
  const [currentProfile, otherProfile] = await Promise.all([
    getProfile(currentUserId),
    getProfile(otherUserId)
  ]);

  if (isAdminFriendship(currentProfile, otherProfile)) {
    return false;
  }

  const { data, error } = await supabase
    .from('friend_requests')
    .select('id')
    .eq('status', 'accepted')
    .or(`and(requester_id.eq.${currentUserId},receiver_id.eq.${otherUserId}),and(requester_id.eq.${otherUserId},receiver_id.eq.${currentUserId})`)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data?.id) {
    return false;
  }

  const { error: updateError } = await supabase
    .from('friend_requests')
    .update({
      status: 'cancelled',
      responded_at: new Date().toISOString()
    })
    .eq('id', data.id);

  return !updateError;
};

export const getPendingFriendRequestCount = async (userId: string): Promise<number> => {
  const { count, error } = await supabase
    .from('friend_requests')
    .select('*', { count: 'exact', head: true })
    .eq('receiver_id', userId)
    .eq('status', 'pending');

  if (error) {
    console.error('Error fetching pending friend request count:', error);
    return 0;
  }

  return count || 0;
};

export const getFriendsOverview = async (userId: string): Promise<{
  friends: Friend[];
  incomingRequests: Array<FriendRequest & { profile: Friend }>;
  outgoingRequests: Array<FriendRequest & { profile: Friend }>;
}> => {
  const [currentProfile, requests] = await Promise.all([
    getProfile(userId),
    getFriendRequestsForUser(userId)
  ]);

  if (!currentProfile) {
    return {
      friends: [],
      incomingRequests: [],
      outgoingRequests: []
    };
  }

  if (normalizeUserRole(currentProfile) === 'admin') {
    const { data } = await supabase
      .from('profiles')
      .select('id, username, display_name, avatar_url, aura_level, total_xp, streak, user_role')
      .neq('id', userId)
      .order('total_xp', { ascending: false });

    return {
      friends: ((data as Partial<Profile>[]) || []).map(buildFriendFromProfile),
      incomingRequests: [],
      outgoingRequests: []
    };
  }

  const relatedIds = new Set<string>();
  requests.forEach((request) => {
    relatedIds.add(request.requester_id);
    relatedIds.add(request.receiver_id);
  });

  const { data: adminProfiles } = await supabase
    .from('profiles')
    .select('id, username, display_name, avatar_url, aura_level, total_xp, streak, user_role')
    .eq('user_role', 'admin');

  adminProfiles?.forEach((profile: any) => relatedIds.add(profile.id));
  relatedIds.delete(userId);

  const ids = Array.from(relatedIds);
  const profilesMap = new Map<string, Friend>();

  if (ids.length) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, username, display_name, avatar_url, aura_level, total_xp, streak, user_role')
      .in('id', ids);

    ((profiles as Partial<Profile>[]) || []).forEach((profile) => {
      profilesMap.set(profile.id || '', buildFriendFromProfile(profile));
    });
  }

  const incomingRequests = requests
    .filter((request) => request.receiver_id === userId && request.status === 'pending')
    .map((request) => ({
      ...request,
      profile: profilesMap.get(request.requester_id) || buildFriendFromProfile({ id: request.requester_id, username: 'desconhecido' })
    }));

  const outgoingRequests = requests
    .filter((request) => request.requester_id === userId && request.status === 'pending')
    .map((request) => ({
      ...request,
      profile: profilesMap.get(request.receiver_id) || buildFriendFromProfile({ id: request.receiver_id, username: 'desconhecido' })
    }));

  const acceptedFriendIds = new Set(
    requests
      .filter((request) => request.status === 'accepted')
      .map((request) => request.requester_id === userId ? request.receiver_id : request.requester_id)
  );

  adminProfiles?.forEach((profile: any) => acceptedFriendIds.add(profile.id));
  acceptedFriendIds.delete(userId);

  const friends = Array.from(acceptedFriendIds)
    .map((id) => profilesMap.get(id))
    .filter(Boolean) as Friend[];

  return {
    friends,
    incomingRequests,
    outgoingRequests
  };
};

type DirectConversationRow = {
  id: string;
  user_a_id: string;
  user_b_id: string;
  last_message_at: string;
  last_message_preview?: string | null;
  last_message_sender_id?: string | null;
  user_a_last_read_at?: string | null;
  user_b_last_read_at?: string | null;
  created_at: string;
  updated_at: string;
};

type DirectMessageRow = {
  id: string;
  conversation_id: string;
  sender_id: string;
  body?: string | null;
  attachment_storage_path?: string | null;
  attachment_mime_type?: string | null;
  created_at: string;
};

export const getPrimaryAdminProfile = async (): Promise<Profile | null> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_role', 'admin')
    .order('total_xp', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error fetching primary admin profile:', error);
    return null;
  }

  return (data as Profile | null) || null;
};

export const getOrCreateDirectConversationId = async (currentUserId: string, targetUserId: string): Promise<string | null> => {
  const { data, error } = await supabase.rpc('get_or_create_direct_conversation', {
    p_user_one: currentUserId,
    p_user_two: targetUserId
  });

  if (error) {
    console.error('Error getting or creating direct conversation:', error);
    return null;
  }

  return (data as string) || null;
};

export const getDirectConversationSummaries = async (userId: string): Promise<DirectConversationSummary[]> => {
  const { data, error } = await supabase
    .from('direct_conversations')
    .select('*')
    .or(`user_a_id.eq.${userId},user_b_id.eq.${userId}`)
    .order('last_message_at', { ascending: false });

  if (error) {
    console.error('Error fetching direct conversations:', error);
    return [];
  }

  const rows = (data as DirectConversationRow[]) || [];
  const peerIds = [...new Set(rows.map((row) => row.user_a_id === userId ? row.user_b_id : row.user_a_id))];

  const { data: peerProfiles, error: peerError } = await supabase
    .from('profiles')
    .select('id, username, display_name, avatar_url, aura_level, total_xp, streak, user_role')
    .in('id', peerIds);

  if (peerError) {
    console.error('Error fetching conversation peers:', peerError);
  }

  const peersMap = new Map<string, Friend>();
  ((peerProfiles as Partial<Profile>[]) || []).forEach((profile) => {
    peersMap.set(profile.id || '', buildFriendFromProfile(profile));
  });

  return rows.map((row) => {
    const peerId = row.user_a_id === userId ? row.user_b_id : row.user_a_id;
    const lastReadAt = row.user_a_id === userId ? row.user_a_last_read_at : row.user_b_last_read_at;
    const hasUnread = Boolean(
      row.last_message_sender_id
      && row.last_message_sender_id !== userId
      && row.last_message_at
      && (!lastReadAt || new Date(row.last_message_at).getTime() > new Date(lastReadAt).getTime())
    );

    return {
      ...row,
      has_unread: hasUnread,
      peer: peersMap.get(peerId) || buildFriendFromProfile({ id: peerId, username: 'desconhecido' })
    };
  });
};

export const markDirectConversationRead = async (conversationId: string, userId: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('mark_direct_conversation_read', {
    p_conversation_id: conversationId,
    p_user_id: userId
  });

  if (error) {
    console.error('Error marking direct conversation as read:', error);
    return false;
  }

  return Boolean(data);
};

export const getUnreadDirectConversationCount = async (userId: string): Promise<number> => {
  const conversations = await getDirectConversationSummaries(userId);
  return conversations.filter((conversation) => conversation.has_unread).length;
};

export const getIncomingDuelCount = async (userId: string): Promise<number> => {
  const { count, error } = await supabase
    .from('friend_duels')
    .select('id', { count: 'exact', head: true })
    .eq('opponent_id', userId)
    .eq('status', 'pending');

  if (error) {
    console.error('Error fetching incoming duel count:', error);
    return 0;
  }

  return count || 0;
};

export const getUnreadAppNotificationCount = async (userId: string): Promise<number> => {
  const { count, error } = await supabase
    .from('app_notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_read', false);

  if (error) {
    logError('notifications', 'Falha ao buscar notificacoes nao lidas do app.', error);
    return 0;
  }

  return count || 0;
};

export const createAppNotification = async (input: {
  user_id: string;
  type: AppNotificationItem['type'];
  title: string;
  message: string;
  metadata?: Record<string, unknown> | null;
  push?: {
    url: string;
    tag?: string;
    kind?: 'badge_unlock' | 'friend_request' | 'direct_message' | 'task_reminder' | 'challenge_invite' | 'challenge_join_request' | 'challenge_join_update' | 'admin_new_signup';
  } | null;
}): Promise<boolean> => {
  const { error } = await supabase
    .from('app_notifications')
    .insert({
      user_id: input.user_id,
      type: input.type,
      title: input.title,
      message: input.message,
      metadata: input.metadata || null,
      is_read: false
    });

  if (error) {
    logError('notifications', 'Falha ao criar notificacao do app.', error);
    return false;
  }

  if (input.push) {
    await triggerPushNotification({
      userIds: [input.user_id],
      title: input.title,
      message: input.message,
      url: input.push.url,
      tag: input.push.tag,
      kind: input.push.kind || 'badge_unlock',
      metadata: input.metadata || null
    });
  }

  return true;
};

export const upsertPushSubscription = async (input: {
  userId: string;
  endpoint: string;
  p256dh: string;
  auth: string;
  userAgent?: string;
}): Promise<boolean> => {
  const { error } = await supabase
    .from('push_subscriptions')
    .upsert({
      user_id: input.userId,
      endpoint: input.endpoint,
      p256dh: input.p256dh,
      auth: input.auth,
      user_agent: input.userAgent || null,
      is_active: true,
      disabled_at: null,
      last_seen_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }, { onConflict: 'endpoint' });

  if (error) {
    logError('push', 'Falha ao registrar assinatura push.', error);
    return false;
  }

  return true;
};

export const disablePushSubscription = async (endpoint: string): Promise<boolean> => {
  const { error } = await supabase
    .from('push_subscriptions')
    .update({
      is_active: false,
      disabled_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .eq('endpoint', endpoint);

  if (error) {
    logError('push', 'Falha ao desativar assinatura push.', error);
    return false;
  }

  return true;
};

export const getActivePushSubscriptionCount = async (userId: string): Promise<number> => {
  const { count, error } = await supabase
    .from('push_subscriptions')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_active', true);

  if (error) {
    logError('push', 'Falha ao contar subscriptions push ativas.', error);
    return 0;
  }

  return count || 0;
};

export const getRecentAppNotifications = async (userId: string, limit: number = 20): Promise<AppNotificationItem[]> => {
  const { data, error } = await supabase
    .from('app_notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    logError('notifications', 'Falha ao carregar notificacoes do app.', error);
    return [];
  }

  return ((data as AppNotificationItem[]) || []);
};

export const markAppNotificationsAsRead = async (userId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('app_notifications')
    .update({
      is_read: true,
      read_at: new Date().toISOString()
    })
    .eq('user_id', userId)
    .eq('is_read', false);

  if (error) {
    logError('notifications', 'Falha ao marcar notificacoes do app como lidas.', error);
    return false;
  }

  return true;
};

export const getAppNotificationSummary = async (userId: string): Promise<AppNotificationSummary> => {
  const [pendingFriendRequests, incomingDuels, unreadDirectConversations, unreadAppNotifications] = await Promise.all([
    getPendingFriendRequestCount(userId),
    getIncomingDuelCount(userId),
    getUnreadDirectConversationCount(userId),
    getUnreadAppNotificationCount(userId)
  ]);

  return {
    pending_friend_requests: pendingFriendRequests,
    incoming_duels: incomingDuels,
    unread_direct_conversations: unreadDirectConversations,
    unread_app_notifications: unreadAppNotifications,
    total: pendingFriendRequests + incomingDuels + unreadDirectConversations + unreadAppNotifications
  };
};

export const getDirectMessages = async (conversationId: string, currentUserId: string): Promise<DirectMessage[]> => {
  const { data: conversationRow, error: conversationError } = await supabase
    .from('direct_conversations')
    .select('*')
    .eq('id', conversationId)
    .maybeSingle();

  if (conversationError || !conversationRow) {
    console.error('Error fetching direct conversation for messages:', conversationError);
    return [];
  }

  const conversation = conversationRow as DirectConversationRow;
  const peerIds = [conversation.user_a_id, conversation.user_b_id];

  const [{ data: messagesData, error: messagesError }, { data: profilesData, error: profilesError }] = await Promise.all([
    supabase
      .from('direct_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true }),
    supabase
      .from('profiles')
      .select('id, username, display_name, avatar_url, aura_level, total_xp, streak, user_role')
      .in('id', peerIds)
  ]);

  if (messagesError) {
    console.error('Error fetching direct messages:', messagesError);
    return [];
  }

  if (profilesError) {
    console.error('Error fetching direct message profiles:', profilesError);
  }

  const profilesMap = new Map<string, Friend>();
  ((profilesData as Partial<Profile>[]) || []).forEach((profile) => {
    profilesMap.set(profile.id || '', buildFriendFromProfile(profile));
  });

  const rows = (messagesData as DirectMessageRow[]) || [];
  return Promise.all(rows.map(async (row) => ({
    ...row,
    attachment_url: row.attachment_storage_path
      ? await getSignedPrivateImageUrl(CHAT_IMAGES_BUCKET, row.attachment_storage_path)
      : null,
    sender: profilesMap.get(row.sender_id),
    is_own_message: row.sender_id === currentUserId
  })));
};

export const sendDirectMessage = async (input: {
  conversationId: string;
  senderId: string;
  body?: string;
  attachmentStoragePath?: string | null;
  attachmentMimeType?: string | null;
}): Promise<{ message: DirectMessage | null; errorMessage?: string }> => {
  const { data: conversationData, error: conversationError } = await supabase
    .from('direct_conversations')
    .select('user_a_id, user_b_id')
    .eq('id', input.conversationId)
    .maybeSingle();

  if (conversationError || !conversationData) {
    console.error('Error fetching direct conversation before sending:', conversationError);
    return {
      message: null,
      errorMessage: 'Não foi possível localizar essa conversa agora.'
    };
  }

  const { data, error } = await supabase.rpc('send_direct_message', {
    p_conversation_id: input.conversationId,
    p_sender_id: input.senderId,
    p_body: input.body?.trim() || null,
    p_attachment_storage_path: input.attachmentStoragePath || null,
    p_attachment_mime_type: input.attachmentMimeType || null
  });

  if (error) {
    console.error('Error sending direct message:', error);
    return {
      message: null,
      errorMessage: error.message || 'Não foi possível registrar essa mensagem agora.'
    };
  }

  // O RPC pode voltar como objeto ou array com 1 item, entao normalizamos os dois formatos.
  const row = (Array.isArray(data) ? data[0] : data) as DirectMessageRow | null;
  if (!row) {
    return {
      message: null,
      errorMessage: 'A mensagem pode não ter sido confirmada pelo servidor.'
    };
  }

  const receiverId = conversationData.user_a_id === input.senderId
    ? conversationData.user_b_id
    : conversationData.user_a_id;
  const senderProfile = await getProfile(input.senderId);
  const preview = input.body?.trim()
    ? input.body.trim().slice(0, 80)
    : 'Enviou uma imagem no chat.';

  if (receiverId && senderProfile) {
    await triggerPushNotification({
      userIds: [receiverId],
      title: senderProfile.display_name || senderProfile.username || 'Nova mensagem',
      message: preview,
      url: `/#/app/chat/${receiverId}`,
      tag: `direct-message:${input.conversationId}`,
      kind: 'direct_message',
      metadata: {
        conversation_id: input.conversationId,
        sender_id: input.senderId
      }
    });
  } else {
    logWarn('push', 'Mensagem enviada sem receiver resolvido para push.', {
      conversationId: input.conversationId,
      senderId: input.senderId
    });
  }

  return {
    message: {
      ...row,
      attachment_url: row.attachment_storage_path
        ? await getSignedPrivateImageUrl(CHAT_IMAGES_BUCKET, row.attachment_storage_path)
        : null,
      is_own_message: true
    }
  };
};

export const getUserSocialSignals = async (
  userIds: string[],
  knownProfileCount?: number
): Promise<Map<string, UserSocialSignals>> => {
  const uniqueUserIds = Array.from(new Set(userIds.filter(Boolean)));
  const socialMap = new Map<string, UserSocialSignals>();

  if (!uniqueUserIds.length) {
    return socialMap;
  }

  uniqueUserIds.forEach((userId) => {
    socialMap.set(userId, {
      user_id: userId,
      friend_count: 0,
      duel_wins: 0,
      active_duels: 0
    });
  });

  const [acceptedRequestsResult, duelsResult, adminProfilesResult, totalProfilesResult] = await Promise.all([
    supabase
      .from('friend_requests')
      .select('requester_id, receiver_id, status')
      .eq('status', 'accepted')
      .or(`requester_id.in.(${uniqueUserIds.join(',')}),receiver_id.in.(${uniqueUserIds.join(',')})`),
    supabase
      .from('friend_duels')
      .select('challenger_id, opponent_id, winner_id, status')
      .or(`challenger_id.in.(${uniqueUserIds.join(',')}),opponent_id.in.(${uniqueUserIds.join(',')})`),
    supabase
      .from('profiles')
      .select('id')
      .eq('user_role', 'admin')
      .in('id', uniqueUserIds),
    knownProfileCount
      ? Promise.resolve({ count: knownProfileCount, error: null })
      : supabase.from('profiles').select('*', { count: 'exact', head: true })
  ]);

  if (!acceptedRequestsResult.error) {
    ((acceptedRequestsResult.data as Array<{ requester_id: string; receiver_id: string }>) || []).forEach((request) => {
      if (socialMap.has(request.requester_id)) {
        socialMap.get(request.requester_id)!.friend_count += 1;
      }
      if (socialMap.has(request.receiver_id)) {
        socialMap.get(request.receiver_id)!.friend_count += 1;
      }
    });
  } else {
    console.error('Error fetching social friend counts:', acceptedRequestsResult.error);
  }

  if (!duelsResult.error) {
    ((duelsResult.data as Array<{ challenger_id: string; opponent_id: string; winner_id?: string | null; status: FriendDuel['status'] }>) || []).forEach((duel) => {
      if ((duel.status === 'pending' || duel.status === 'active')) {
        if (socialMap.has(duel.challenger_id)) {
          socialMap.get(duel.challenger_id)!.active_duels += 1;
        }
        if (socialMap.has(duel.opponent_id)) {
          socialMap.get(duel.opponent_id)!.active_duels += 1;
        }
      }

      if (duel.status === 'completed' && duel.winner_id && socialMap.has(duel.winner_id)) {
        socialMap.get(duel.winner_id)!.duel_wins += 1;
      }
    });
  } else {
    console.error('Error fetching social duel counts:', duelsResult.error);
  }

  const totalProfiles = totalProfilesResult.error ? uniqueUserIds.length : Math.max(0, (totalProfilesResult.count || 0) - 1);
  ((adminProfilesResult.data as Array<{ id: string }>) || []).forEach((adminProfile) => {
    const currentSignals = socialMap.get(adminProfile.id);
    if (currentSignals) {
      currentSignals.friend_count = totalProfiles;
    }
  });

  return socialMap;
};

export const getFriendDuels = async (userId: string): Promise<FriendDuel[]> => {
  const { data, error } = await supabase
    .from('friend_duels')
    .select('*')
    .or(`challenger_id.eq.${userId},opponent_id.eq.${userId}`)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching friend duels:', error);
    return [];
  }

  const rows = ((data as FriendDuelRow[]) || []);
  const now = new Date();
  const duelIdsToExpire = rows
    .filter((row) => (row.status === 'pending' || row.status === 'active') && new Date(row.ends_at) < now)
    .map((row) => row.id);

  if (duelIdsToExpire.length) {
    const expiredAt = new Date().toISOString();
    const { error: expireError } = await supabase
      .from('friend_duels')
      .update({
        status: 'expired',
        resolved_at: expiredAt,
        updated_at: expiredAt
      })
      .in('id', duelIdsToExpire);

    if (expireError) {
      console.error('Error expiring friend duels:', expireError);
    } else {
      rows.forEach((row) => {
        if (duelIdsToExpire.includes(row.id)) {
          row.status = 'expired';
          row.resolved_at = expiredAt;
          row.updated_at = expiredAt;
        }
      });
    }
  }

  const profileIds = Array.from(new Set(rows.flatMap((row) => [row.challenger_id, row.opponent_id])));
  const profilesMap = new Map<string, Friend>();

  if (profileIds.length) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, username, display_name, avatar_url, aura_level, total_xp, streak, user_role')
      .in('id', profileIds);

    ((profiles as Partial<Profile>[]) || []).forEach((profile) => {
      profilesMap.set(profile.id || '', buildFriendFromProfile(profile));
    });
  }

  return rows.map((row) => mapFriendDuelRow(row, profilesMap, userId));
};

export const createFriendDuel = async (input: {
  challengerId: string;
  opponentId: string;
  title: string;
  description?: string;
  stakeCredits: number;
  durationDays: number;
}): Promise<string | null> => {
  if (input.challengerId === input.opponentId) {
    return null;
  }

  const [friendshipStatus, challengerProfile, opponentProfile] = await Promise.all([
    getFriendshipStatus(input.challengerId, input.opponentId),
    getProfile(input.challengerId),
    getProfile(input.opponentId)
  ]);

  if (friendshipStatus !== 'friends' || !challengerProfile || !opponentProfile) {
    return null;
  }

  const stakeCredits = Math.max(0, Math.round(input.stakeCredits));
  if (challengerProfile.credits < stakeCredits) {
    return null;
  }

  const nowIso = new Date().toISOString();
  const endsAt = addDays(new Date(), Math.max(1, input.durationDays)).toISOString();

  const { data, error } = await supabase
    .from('friend_duels')
    .insert({
      challenger_id: input.challengerId,
      opponent_id: input.opponentId,
      title: input.title.trim(),
      description: input.description?.trim() || null,
      stake_credits: stakeCredits,
      duration_days: Math.max(1, input.durationDays),
      status: 'pending',
      starts_at: nowIso,
      ends_at: endsAt,
      updated_at: nowIso
    })
    .select('id')
    .single();

  if (error || !data?.id) {
    console.error('Error creating friend duel:', error);
    return null;
  }

  return data.id;
};

export const respondToFriendDuel = async (
  duelId: string,
  userId: string,
  action: 'accepted' | 'rejected' | 'cancelled'
): Promise<boolean> => {
  const { data: duel, error: duelError } = await supabase
    .from('friend_duels')
    .select('*')
    .eq('id', duelId)
    .maybeSingle();

  if (duelError || !duel) {
    console.error('Error loading friend duel to respond:', duelError);
    return false;
  }

  if (duel.status !== 'pending') {
    return false;
  }

  const nowIso = new Date().toISOString();

  if (action === 'accepted') {
    if (duel.opponent_id !== userId) {
      return false;
    }

    const { error } = await supabase
      .from('friend_duels')
      .update({
        status: 'active',
        accepted_at: nowIso,
        updated_at: nowIso
      })
      .eq('id', duelId);

    if (error) {
      console.error('Error accepting friend duel:', error);
      return false;
    }

    return true;
  }

  const canCancel = duel.challenger_id === userId || duel.opponent_id === userId;
  if (!canCancel) {
    return false;
  }

  const { error } = await supabase
    .from('friend_duels')
    .update({
      status: action,
      resolved_at: nowIso,
      updated_at: nowIso
    })
    .eq('id', duelId);

  if (error) {
    console.error(`Error updating friend duel with action ${action}:`, error);
    return false;
  }

  return true;
};

export const resolveFriendDuel = async (input: {
  duelId: string;
  actorId: string;
  winnerId: string;
  resolutionNotes?: string;
}): Promise<boolean> => {
  const { data: duel, error: duelError } = await supabase
    .from('friend_duels')
    .select('*')
    .eq('id', input.duelId)
    .maybeSingle();

  if (duelError || !duel) {
    console.error('Error loading friend duel to resolve:', duelError);
    return false;
  }

  if (duel.status !== 'active') {
    return false;
  }

  const participantIds = [duel.challenger_id, duel.opponent_id];
  if (!participantIds.includes(input.actorId) || !participantIds.includes(input.winnerId)) {
    return false;
  }

  const loserId = duel.challenger_id === input.winnerId ? duel.opponent_id : duel.challenger_id;
  const [winnerProfile, loserProfile] = await Promise.all([
    getProfile(input.winnerId),
    getProfile(loserId)
  ]);

  if (!winnerProfile || !loserProfile) {
    return false;
  }

  const settledCredits = Math.max(0, Math.min(duel.stake_credits || 0, loserProfile.credits || 0));
  const nowIso = new Date().toISOString();

  if (settledCredits > 0) {
    const [winnerUpdated, loserUpdated] = await Promise.all([
      updateProfile(input.winnerId, { credits: (winnerProfile.credits || 0) + settledCredits }),
      updateProfile(loserId, { credits: Math.max(0, (loserProfile.credits || 0) - settledCredits) })
    ]);

    if (!winnerUpdated || !loserUpdated) {
      return false;
    }
  }

  const { error } = await supabase
    .from('friend_duels')
    .update({
      status: 'completed',
      winner_id: input.winnerId,
      resolved_at: nowIso,
      settled_credits: settledCredits,
      resolution_notes: input.resolutionNotes?.trim() || null,
      updated_at: nowIso
    })
    .eq('id', input.duelId);

  if (error) {
    console.error('Error resolving friend duel:', error);
    return false;
  }

  return true;
};

export const updateProfile = async (userId: string, updates: Partial<Profile>): Promise<boolean> => {
  const { error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId);

  if (error) {
    console.error('Error updating profile:', error);
    return false;
  }

  return true;
};

// Task operations
export const getTasks = async (userId: string): Promise<Task[]> => {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('user_id', userId)
    .eq('active', true)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching tasks:', error);
    return [];
  }

  return ((data as Task[]) || []).map((task) => {
    const completionIsValid = isCompletionStillValid(task);

    if (completionIsValid) {
      return task;
    }

    return {
      ...task,
      is_completed: false,
      completed_at: isRepeatingTask(task) ? null : task.completed_at
    };
  });
};

export const createTask = async (task: Omit<Task, 'id'> & { user_id: string }): Promise<Task | null> => {
  const taskPayload = {
    ...task,
    xp: USER_TASK_FIXED_XP
  };

  const { data, error } = await supabase
    .from('tasks')
    .insert(taskPayload)
    .select()
    .single();

  if (error) {
    console.error('Error creating task:', error);
    return null;
  }

  return data;
};

export const updateTask = async (taskId: string, updates: Partial<Task>): Promise<boolean> => {
  const updatePayload = {
    ...updates,
    xp: USER_TASK_FIXED_XP
  };

  const { error } = await supabase
    .from('tasks')
    .update(updatePayload)
    .eq('id', taskId);

  if (error) {
    console.error('Error updating task:', error);
    return false;
  }

  return true;
};

export const deleteTask = async (taskId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('tasks')
    .update({ active: false })
    .eq('id', taskId);

  if (error) {
    console.error('Error deleting task:', error);
    return false;
  }

  return true;
};

export const refreshRecurringTasks = async (userId: string): Promise<void> => {
  const { data, error } = await supabase
    .from('tasks')
    .select('id, frequency, task_type, is_completed, completed_at')
    .eq('user_id', userId)
    .eq('active', true);

  if (error) {
    console.error('Error refreshing recurring tasks:', error);
    return;
  }

  const now = new Date();
  const today = getCurrentDateOnly();
  const currentWeekStart = getWeekStartDateOnly(now);
  const currentMonthStart = getMonthStartDateOnly(now);
  const taskIdsToReset = (data || [])
    .filter((task) => {
      if (!task.is_completed) return false;
      if (!isRepeatingTask(task)) return false;

      const completedDate = toDateOnly(task.completed_at);
      if (!completedDate) return true;

      if (task.frequency === 'daily') {
        return completedDate < today;
      }

      if (task.frequency === 'weekly') {
        return completedDate < currentWeekStart;
      }

      if (task.frequency === 'monthly') {
        return completedDate < currentMonthStart;
      }

      return false;
    })
    .map((task) => task.id);

  if (!taskIdsToReset.length) {
    return;
  }

  // Comentario em pt-BR: aqui reabrimos so as tarefas recorrentes que ja passaram do dia atual.
  const { error: updateError } = await supabase
    .from('tasks')
    .update({
      is_completed: false,
      completed_at: null
    })
    .in('id', taskIdsToReset);

  if (updateError) {
    console.error('Error resetting recurring tasks:', updateError);
  }
};

export const createTaskCompletion = async (
  completion: Omit<TaskCompletion, 'id' | 'created_at' | 'completed_at'> & {
    completed_at?: string;
  }
): Promise<TaskCompletion | null> => {
  const payload = {
    ...completion,
    completed_at: completion.completed_at || new Date().toISOString()
  };

  const { data, error } = await supabase
    .from('task_completions')
    .insert(payload)
    .select('*')
    .single();

  if (error) {
    console.error('Error creating task completion:', error);
    return null;
  }

  return data as TaskCompletion;
};

export const getTaskCompletions = async (userId: string, taskId?: string): Promise<TaskCompletion[]> => {
  let query = supabase
    .from('task_completions')
    .select('*')
    .eq('user_id', userId)
    .order('completed_at', { ascending: false });

  if (taskId) {
    query = query.eq('task_id', taskId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching task completions:', error);
    return [];
  }

  const completions = (data as TaskCompletion[]) || [];
  const resolved = await Promise.all(
    completions.map(async (completion) => ({
      ...completion,
      evidence_url: completion.evidence_storage_path
        ? await getSignedPrivateImageUrl(TASK_EVIDENCE_BUCKET, completion.evidence_storage_path)
        : completion.evidence_url || null
    }))
  );

  return resolved;
};

export const getTaskCompletionFeed = async (userId: string, limit: number = 20): Promise<TaskCompletion[]> => {
  const { data, error } = await supabase
    .from('task_completions')
    .select('*')
    .eq('user_id', userId)
    .order('completed_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching task completion feed:', error);
    return [];
  }

  const completions = (data as TaskCompletion[]) || [];
  const resolved = await Promise.all(
    completions.map(async (completion) => ({
      ...completion,
      evidence_url: completion.evidence_storage_path
        ? await getSignedPrivateImageUrl(TASK_EVIDENCE_BUCKET, completion.evidence_storage_path)
        : completion.evidence_url || null
    }))
  );

  return resolved;
};

export const getJournalEntries = async (userId: string): Promise<SystemLogEntry[]> => {
  const { data, error } = await supabase
    .from('journal_entries')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching journal entries:', error);
    return [];
  }

  const entries = (data as any[]) || [];
  return Promise.all(entries.map(async (entry) => ({
    id: entry.id,
    user_id: entry.user_id,
    date: entry.created_at,
    content: entry.content,
    ai_feedback: entry.ai_feedback || '',
    image_storage_path: entry.image_storage_path || null,
    image_expires_at: entry.image_expires_at || null,
    image_url: entry.image_storage_path
      ? await getSignedPrivateImageUrl(JOURNAL_IMAGES_BUCKET, entry.image_storage_path)
      : null
  })));
};

export const createJournalEntry = async (entry: {
  user_id: string;
  content: string;
  ai_feedback?: string;
  image_storage_path?: string | null;
  image_expires_at?: string | null;
}): Promise<SystemLogEntry | null> => {
  const { data, error } = await supabase
    .from('journal_entries')
    .insert({
      user_id: entry.user_id,
      content: entry.content,
      ai_feedback: entry.ai_feedback || null,
      image_storage_path: entry.image_storage_path || null,
      image_expires_at: entry.image_expires_at || null,
      updated_at: new Date().toISOString()
    })
    .select('*')
    .single();

  if (error || !data) {
    console.error('Error creating journal entry:', error);
    return null;
  }

  return {
    id: data.id,
    user_id: data.user_id,
    date: data.created_at,
    content: data.content,
    ai_feedback: data.ai_feedback || '',
    image_storage_path: data.image_storage_path || null,
    image_expires_at: data.image_expires_at || null,
    image_url: data.image_storage_path
      ? await getSignedPrivateImageUrl(JOURNAL_IMAGES_BUCKET, data.image_storage_path)
      : null
  };
};

// Challenge operations
const mapChallengeRow = (
  row: any,
  participantCount: number,
  currentParticipant?: any,
  currentRequest?: any,
  currentUserId?: string | null,
  participantRank?: number | null
): Challenge => {
  const status = getChallengeStatus(row);

  return {
    id: row.id,
    title: row.title,
    description: row.description,
    visibility: row.visibility === 'private' ? 'private' : 'public',
    rules_text: row.rules_text || null,
    private_access_token: row.private_access_token || null,
    private_access_code: row.private_access_code || null,
    allow_late_join: row.allow_late_join !== false,
    entry_mode: row.entry_mode === 'paid' ? 'paid' : 'free',
    entry_cost: Number(row.entry_cost || 0),
    prize_pool_credits: Number(row.prize_pool_credits || 0),
    payout_completed_at: row.payout_completed_at || null,
    duration_days: row.duration_days || 0,
    difficulty: row.difficulty,
    reward_xp: 0,
    pro_only: false,
    created_by: row.created_by || undefined,
    starts_at: row.starts_at || null,
    ends_at: row.ends_at || null,
    is_perennial: !!row.is_perennial,
    participants: participantCount,
    status,
    joined: !!currentParticipant && currentParticipant.status !== 'left',
    is_creator: Boolean(currentUserId && row.created_by === currentUserId),
    participant_id: currentParticipant?.id || null,
    participant_status: currentParticipant?.status || undefined,
    participant_progress: currentParticipant?.progress_percent || 0,
    participant_rank: participantRank ?? null,
    result_message: currentParticipant?.result_message || null,
    private_access_request_status: currentRequest?.status || null,
    private_access_request_id: currentRequest?.id || null,
    private_access_review_note: currentRequest?.review_note || null
  };
};

const pickCurrentChallengeParticipant = (rows: any[] = []) => {
  if (!rows.length) {
    return null;
  }

  const statusPriority: Record<string, number> = {
    active: 0,
    completed: 1,
    failed: 2,
    left: 3
  };

  return [...rows].sort((a, b) => {
    const statusGap = (statusPriority[a.status] ?? 99) - (statusPriority[b.status] ?? 99);
    if (statusGap !== 0) {
      return statusGap;
    }

    const joinedAtGap = new Date(b.joined_at || 0).getTime() - new Date(a.joined_at || 0).getTime();
    if (joinedAtGap !== 0) {
      return joinedAtGap;
    }

    return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
  })[0];
};

const settleChallengeCreditPoolIfNeeded = async (challenge: Pick<Challenge, 'id' | 'entry_mode' | 'status' | 'payout_completed_at'>) => {
  if (challenge.entry_mode !== 'paid' || challenge.status !== 'ended_recently' || challenge.payout_completed_at) {
    return;
  }

  const { error } = await supabase.rpc('settle_challenge_credit_pool', {
    p_challenge_id: challenge.id
  });

  if (error) {
    logError('challenges', 'Falha ao liquidar o pote de moedas do challenge.', {
      challengeId: challenge.id,
      error
    });
    return;
  }

  logInfo('challenges', 'Pote de moedas do challenge liquidado ou confirmado.', {
    challengeId: challenge.id
  });
};

const syncChallengeParticipantProgress = async (participantId: string): Promise<void> => {
  const { error: rpcError } = await supabase.rpc('sync_challenge_participant_progress_state', {
    p_participant_id: participantId
  });

  if (!rpcError) {
    return;
  }

  logWarn('challenges', 'RPC oficial do challenge indisponivel. Aplicando reconciliacao local de fallback.', {
    participantId,
    error: rpcError
  });

  const { data: participant } = await supabase
    .from('challenge_participants')
    .select('id, challenge_id, status, joined_at')
    .eq('id', participantId)
    .maybeSingle();

  if (!participant) {
    return;
  }

  const [{ data: challengeTasks }, { data: completions }, { data: challenge }] = await Promise.all([
    supabase.from('challenge_tasks').select('*').eq('challenge_id', participant.challenge_id),
    supabase.from('challenge_task_completions').select('*').eq('participant_id', participantId),
    supabase.from('challenges').select('id, title, ends_at, is_perennial, starts_at, difficulty').eq('id', participant.challenge_id).maybeSingle()
  ]);

  const challengeTaskRows = (challengeTasks as ChallengeTask[]) || [];
  const completionRows = ((completions as ChallengeTaskCompletion[]) || []).filter((completion) => completion.evidence_status !== 'rejected');
  const completionsByTask = new Map<string, ChallengeTaskCompletion[]>();

  completionRows.forEach((completion) => {
    const taskCompletions = completionsByTask.get(completion.challenge_task_id) || [];
    taskCompletions.push(completion);
    completionsByTask.set(completion.challenge_task_id, taskCompletions);
  });

  const totalExpected = challengeTaskRows.reduce((sum, task) => sum + getChallengeTaskTarget(task), 0);
  const totalDone = challengeTaskRows.reduce((sum, task) => {
    const progress = getChallengeTaskProgress(task, completionsByTask.get(task.id) || []);
    return sum + Math.min(progress.totalCompleted, progress.targetCompletions);
  }, 0);
  const progressPercent = totalExpected > 0 ? Math.round((totalDone / totalExpected) * 100) : 0;
  const taskCount = challengeTaskRows.length;
  const doneCount = totalDone;

  let nextStatus: 'active' | 'completed' | 'failed' | 'left' = participant.status === 'left' ? 'left' : 'active';
  let resultMessage: string | null = null;
  let completedAt: string | null = null;
  let hardModeMissedDay: string | null = null;

  if (nextStatus !== 'left' && challenge?.difficulty === 'Hard Mode') {
    const dailyTasks = challengeTaskRows.filter((task) => task.frequency === 'daily');
    const joinedAtDate = getLocalDateOnlyFromValue(participant.joined_at);
    const challengeStartDate = getLocalDateOnlyFromValue(challenge?.starts_at || null);
    const todayDate = getTodayLocalDateOnly();

    if (dailyTasks.length && joinedAtDate && challengeStartDate && challengeStartDate <= todayDate) {
      const rangeStart = new Date(`${joinedAtDate > challengeStartDate ? joinedAtDate : challengeStartDate}T12:00:00`);
      const rangeEndDate = challenge?.ends_at
        ? getLocalDateOnlyFromValue(challenge.ends_at) || todayDate
        : todayDate;
      const challengeEndedBeforeToday = Boolean(challenge?.ends_at && rangeEndDate < todayDate);
      const evaluationEnd = challengeEndedBeforeToday
        ? new Date(`${rangeEndDate}T12:00:00`)
        : new Date(`${todayDate}T12:00:00`);
      evaluationEnd.setDate(evaluationEnd.getDate() - 1);

      if (evaluationEnd >= rangeStart) {
        const dailyTaskIds = new Set(dailyTasks.map((task) => task.id));
        const completedByDay = new Map<string, Set<string>>();

        completionRows.forEach((completion) => {
          if (!dailyTaskIds.has(completion.challenge_task_id)) {
            return;
          }

          const completionDay = completion.completion_date || getLocalDateOnlyFromValue(completion.completed_at);
          if (!completionDay) {
            return;
          }

          const currentDaySet = completedByDay.get(completionDay) || new Set<string>();
          currentDaySet.add(completion.challenge_task_id);
          completedByDay.set(completionDay, currentDaySet);
        });

        const cursorDate = new Date(rangeStart);
        while (cursorDate <= evaluationEnd) {
          const cursorKey = getLocalDateOnlyFromValue(cursorDate.toISOString());
          if (cursorKey && (completedByDay.get(cursorKey)?.size || 0) < dailyTasks.length) {
            hardModeMissedDay = cursorKey;
            break;
          }
          cursorDate.setDate(cursorDate.getDate() + 1);
        }
      }
    }
  }

  if (nextStatus !== 'left' && hardModeMissedDay) {
    nextStatus = 'failed';
    resultMessage = `Hard Mode quebrado. Você foi eliminado por não concluir todas as tarefas diárias em ${new Date(`${hardModeMissedDay}T12:00:00`).toLocaleDateString('pt-BR')}.`;
  } else if (nextStatus !== 'left' && taskCount > 0 && totalExpected > 0 && totalDone >= totalExpected) {
    nextStatus = 'completed';
    completedAt = new Date().toISOString();
    resultMessage = `Challenge concluído: ${challenge?.title || 'Challenge'} finalizado com sucesso.`;
  } else if (nextStatus !== 'left' && challenge?.ends_at && new Date(challenge.ends_at) < new Date()) {
    nextStatus = 'failed';
    resultMessage = `Prazo encerrado. Você concluiu ${doneCount} de ${taskCount} tarefas do challenge.`;
  }

  await supabase
    .from('challenge_participants')
    .update({
      progress_percent: progressPercent,
      status: nextStatus,
      completed_at: completedAt,
      result_message: resultMessage
    })
    .eq('id', participantId);
};

const resolveChallengeCompletionEvidence = async (
  completion: ChallengeTaskCompletion
): Promise<ChallengeTaskCompletion> => ({
  ...completion,
  evidence_url: completion.evidence_storage_path
    ? await getSignedPrivateImageUrl(TASK_EVIDENCE_BUCKET, completion.evidence_storage_path)
    : completion.evidence_url || null
});

export const getChallenges = async (userId?: string | null): Promise<Challenge[]> => {
  const startedAt = typeof window !== 'undefined' && window.performance ? window.performance.now() : Date.now();
  const { data, error } = await supabase
    .from('challenges')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    logError('challenges', 'Falha ao buscar a lista base de challenges.', error);
    return [];
  }

  const challengeRows = (data as any[]) || [];
  const challengeIds = challengeRows.map((row) => row.id);

  const [participantCountsResponse, currentParticipantsResponse, directCurrentParticipantsResponse, currentRequestsResponse] = await Promise.all([
    challengeIds.length
      ? supabase.rpc('get_challenge_live_participant_counts')
      : Promise.resolve({ data: [], error: null } as any),
    userId && challengeIds.length
      ? supabase.rpc('get_my_joined_challenge_participants')
      : Promise.resolve({ data: [], error: null } as any),
    userId && challengeIds.length
      ? supabase
          .from('challenge_participants')
          .select('*')
          .eq('user_id', userId)
          .in('challenge_id', challengeIds)
          .in('status', ['active', 'completed', 'failed'])
      : Promise.resolve({ data: [], error: null } as any),
    userId && challengeIds.length
      ? supabase
          .from('challenge_join_requests')
          .select('*')
          .eq('requester_user_id', userId)
          .in('challenge_id', challengeIds)
      : Promise.resolve({ data: [], error: null } as any)
  ]);

  if (participantCountsResponse.error) {
    logError('challenges', 'Falha ao contar participantes dos challenges.', participantCountsResponse.error);
  }

  if (currentParticipantsResponse.error) {
    logError('challenges', 'Falha ao buscar status do usuario nos challenges.', currentParticipantsResponse.error);
  }

  if (directCurrentParticipantsResponse.error) {
    logError('challenges', 'Falha ao buscar participacoes diretas do usuario nos challenges.', directCurrentParticipantsResponse.error);
  }

  if (currentRequestsResponse.error) {
    logError('challenges', 'Falha ao buscar pedidos privados do usuario nos challenges.', currentRequestsResponse.error);
  }

  const countsMap = new Map<string, number>();
  if (participantCountsResponse.error && challengeIds.length) {
    const { data: fallbackParticipantRows, error: fallbackParticipantCountsError } = await supabase
      .from('challenge_participants')
      .select('challenge_id, status')
      .in('challenge_id', challengeIds)
      .in('status', ['active', 'completed']);

    if (fallbackParticipantCountsError) {
      logError('challenges', 'Fallback da contagem de participantes dos challenges tambem falhou.', fallbackParticipantCountsError);
    } else {
      ((fallbackParticipantRows as any[]) || []).forEach((row) => {
        countsMap.set(row.challenge_id, (countsMap.get(row.challenge_id) || 0) + 1);
      });
    }
  } else {
    ((participantCountsResponse.data as any[]) || []).forEach((row) => {
      countsMap.set(row.challenge_id, Number(row.live_participants || 0));
    });
  }

  let currentParticipantRows = [
    ...((currentParticipantsResponse.data as any[]) || []),
    ...((directCurrentParticipantsResponse.data as any[]) || [])
  ];
  currentParticipantRows = currentParticipantRows.filter((row, index, collection) => {
    return collection.findIndex((candidate) => candidate.id === row.id) === index;
  });
  if (currentParticipantsResponse.error && userId && challengeIds.length) {
    const { data: fallbackCurrentParticipantRows, error: fallbackCurrentParticipantsError } = await supabase
      .from('challenge_participants')
      .select('*')
      .eq('user_id', userId)
      .in('challenge_id', challengeIds)
      .in('status', ['active', 'completed', 'failed']);

    if (fallbackCurrentParticipantsError) {
      logError('challenges', 'Fallback da leitura do usuario nos challenges tambem falhou.', fallbackCurrentParticipantsError);
    } else {
      currentParticipantRows = (fallbackCurrentParticipantRows as any[]) || [];
    }
  }

  const participantRowsByChallenge = new Map<string, any[]>();
  currentParticipantRows
    .filter((row) => challengeIds.includes(row.challenge_id))
    .forEach((row) => {
      const rows = participantRowsByChallenge.get(row.challenge_id) || [];
      rows.push(row);
      participantRowsByChallenge.set(row.challenge_id, rows);
    });

  const participantMap = new Map<string, any>();
  participantRowsByChallenge.forEach((rows, challengeId) => {
    const currentParticipant = pickCurrentChallengeParticipant(rows);
    if (currentParticipant) {
      participantMap.set(challengeId, currentParticipant);
    }
  });

  const requestMap = new Map<string, any>();
  ((currentRequestsResponse.data as any[]) || []).forEach((row) => {
    requestMap.set(row.challenge_id, row);
  });

  const preliminaryChallenges = challengeRows
    .filter((row) => {
      const visibility = row.visibility === 'private' ? 'private' : 'public';
      if (visibility === 'public') {
        return true;
      }

      if (!userId) {
        return false;
      }

      return row.created_by === userId || participantMap.has(row.id) || requestMap.has(row.id);
    })
    .map((row) => mapChallengeRow(row, countsMap.get(row.id) || 0, participantMap.get(row.id), requestMap.get(row.id), userId))
    .filter((challenge) => challenge.status !== 'archived');

  const joinedChallenges = preliminaryChallenges.filter((challenge) => challenge.joined && challenge.participant_id);
  const rankEntries = await Promise.all(
    joinedChallenges.map(async (challenge) => {
      const leaderboard = await getChallengeLeaderboard(challenge.id);
      const currentEntry = leaderboard.find((entry) => entry.participant_id === challenge.participant_id);
      return [challenge.id, currentEntry?.rank || null] as const;
    })
  );
  const rankMap = new Map<string, number | null>(rankEntries);

  const mappedChallenges = preliminaryChallenges.map((challenge) => ({
    ...challenge,
    participant_rank: rankMap.get(challenge.id) ?? challenge.participant_rank ?? null
  }));

  await Promise.all(mappedChallenges.map((challenge) => settleChallengeCreditPoolIfNeeded(challenge)));

  const durationMs = Math.round(((typeof window !== 'undefined' && window.performance ? window.performance.now() : Date.now()) - startedAt));
  logInfo('challenges', 'Challenges carregados do Supabase.', {
    userId: userId || null,
        totalChallenges: mappedChallenges.length,
        participantRows: ((participantCountsResponse.data as any[]) || []).length,
        currentUserRows: currentParticipantRows.length,
        currentRequestRows: ((currentRequestsResponse.data as any[]) || []).length,
        durationMs
      });

  return mappedChallenges;
};

export const getChallengeDetail = async (
  challengeId: string,
  userId?: string | null,
  accessToken?: string | null
): Promise<ChallengeDetailData | null> => {
  const startedAt = typeof window !== 'undefined' && window.performance ? window.performance.now() : Date.now();
  const { data: challengeRow, error } = await supabase
    .from('challenges')
    .select('*')
    .eq('id', challengeId)
    .maybeSingle();

  if (error || !challengeRow) {
    logError('challenge-detail', 'Falha ao buscar o registro base do challenge.', { challengeId, error });
    return null;
  }

  const visibility = challengeRow.visibility === 'private' ? 'private' : 'public';
  const isCreator = Boolean(userId && challengeRow.created_by === userId);
  const hasMatchingPrivateLink = Boolean(
    accessToken &&
    (
      (challengeRow.private_access_token && accessToken === challengeRow.private_access_token)
      || (challengeRow.private_access_code && accessToken.toUpperCase() === String(challengeRow.private_access_code).toUpperCase())
    )
  );

  const [{ data: participantRows, error: participantRowsError }, { data: taskRows, error: taskRowsError }, { data: requestRows, error: requestRowsError }] = await Promise.all([
    userId
      ? supabase.rpc('get_my_challenge_participant', { p_challenge_id: challengeId })
      : Promise.resolve({ data: [] } as any),
    supabase.from('challenge_tasks').select('*').eq('challenge_id', challengeId).order('position', { ascending: true }).order('created_at', { ascending: true })
    ,
    userId
      ? supabase.from('challenge_join_requests').select('*').eq('challenge_id', challengeId).eq('requester_user_id', userId)
      : Promise.resolve({ data: [] } as any)
  ]);

  if (participantRowsError) {
    logError('challenge-detail', 'Falha ao buscar participacao atual do usuario no challenge.', { challengeId, userId, error: participantRowsError });
  }

  if (taskRowsError) {
    logError('challenge-detail', 'Falha ao buscar tasks do challenge.', { challengeId, error: taskRowsError });
  }

  if (requestRowsError) {
    logError('challenge-detail', 'Falha ao buscar o pedido privado do usuario no challenge.', { challengeId, userId, error: requestRowsError });
  }

  let participants = (participantRows as any[]) || [];
  let currentParticipant = pickCurrentChallengeParticipant(participants);
  const currentRequest = ((requestRows as any[]) || [])[0];

  if (visibility === 'private' && !isCreator && !currentParticipant && !currentRequest && !hasMatchingPrivateLink) {
    logWarn('challenge-detail', 'Tentativa de abrir challenge privado sem permissao.', {
      challengeId,
      userId: userId || null
    });
    return null;
  }

  if (currentParticipant?.id && currentParticipant.status !== 'left') {
    await syncChallengeParticipantProgress(currentParticipant.id);
    const { data: refreshedParticipants } = await supabase.rpc('get_my_challenge_participant', {
      p_challenge_id: challengeId
    });

    participants = (refreshedParticipants as any[]) || participants;
    currentParticipant = pickCurrentChallengeParticipant(participants);
  }

  const { data: liveParticipantCounts, error: liveParticipantCountsError } = await supabase
    .rpc('get_challenge_live_participant_counts');

  if (liveParticipantCountsError) {
    logError('challenge-detail', 'Falha ao buscar total de participantes vivos do challenge.', { challengeId, error: liveParticipantCountsError });
  }

  const participantCount = (((liveParticipantCounts as Array<{ challenge_id: string; live_participants: number }>) || [])
    .find((row) => row.challenge_id === challengeId)?.live_participants) || 0;

  const completionsByTask = new Map<string, ChallengeTaskCompletion[]>();
  if (currentParticipant?.id) {
    let completions: ChallengeTaskCompletion[] | null = null;
    const { data: completionRowsRpc, error: completionsRpcError } = await supabase.rpc('get_my_challenge_task_completions', {
      p_challenge_id: challengeId
    });

    if (completionsRpcError) {
      logWarn('challenge-detail', 'RPC de conclusoes do challenge indisponivel. Voltando para leitura direta.', {
        challengeId,
        participantId: currentParticipant.id,
        error: completionsRpcError
      });

      const { data: fallbackRows, error: completionsError } = await supabase
        .from('challenge_task_completions')
        .select('*')
        .eq('participant_id', currentParticipant.id);

      if (completionsError) {
        logError('challenge-detail', 'Falha ao buscar completions do participante no challenge.', {
          challengeId,
          participantId: currentParticipant.id,
          error: completionsError
        });
      }

      completions = (fallbackRows as ChallengeTaskCompletion[]) || [];
    } else {
      completions = ((completionRowsRpc as ChallengeTaskCompletion[]) || []).filter(
        (completion) => completion.participant_id === currentParticipant.id
      );
    }

    (completions || []).forEach((completion) => {
      const taskCompletions = completionsByTask.get(completion.challenge_task_id) || [];
      taskCompletions.push(completion);
      completionsByTask.set(completion.challenge_task_id, taskCompletions);
    });
  }

  const result = {
    challenge: mapChallengeRow(challengeRow, participantCount, currentParticipant, currentRequest, userId),
    tasks: ((taskRows as ChallengeTask[]) || []).map((task) => ({
      ...task,
      target_completions: getChallengeTaskTarget(task),
      ...(() => {
        const taskCompletions = completionsByTask.get(task.id) || [];
        const validTaskCompletions = taskCompletions.filter((completion) => completion.evidence_status !== 'rejected');
        const progress = getChallengeTaskProgress(task, validTaskCompletions);
        return {
          is_completed: progress.isCompletedInCurrentCycle,
          is_fully_completed: progress.isFullyCompleted,
          completion_id: validTaskCompletions[validTaskCompletions.length - 1]?.id || null,
          total_completed: progress.totalCompleted,
          current_cycle_completions: progress.currentCycleCompletions,
          current_cycle_key: progress.currentCycleKey,
          cycle_label: progress.cycleLabel,
          can_check_today: !progress.isCompletedInCurrentCycle && !progress.isFullyCompleted
        };
      })()
    }))
  };

  await settleChallengeCreditPoolIfNeeded(result.challenge);

  const durationMs = Math.round(((typeof window !== 'undefined' && window.performance ? window.performance.now() : Date.now()) - startedAt));
  logInfo('challenge-detail', 'Challenge detail carregado do Supabase.', {
    challengeId,
    userId: userId || null,
    participantId: currentParticipant?.id || null,
    tasks: result.tasks.length,
    completionsLoaded: Array.from(completionsByTask.values()).reduce((sum, entries) => sum + entries.length, 0),
    durationMs
  });

  return result;
};

export const joinChallenge = async (
  challengeId: string,
  userId: string,
  accessToken?: string | null
): Promise<ChallengeJoinResult> => {
  const { data, error } = await supabase.rpc('join_challenge_with_rules', {
    p_challenge_id: challengeId,
    p_access_token: accessToken || null
  });

  if (error) {
    logError('challenges', 'Falha ao entrar no challenge com regras de acesso.', {
      challengeId,
      userId,
      error
    });
    return {
      ok: false,
      message: 'Não foi possível registrar sua entrada nesse challenge agora.'
    };
  }

  return (data as ChallengeJoinResult) || {
    ok: false,
    message: 'Challenge sem resposta valida no momento.'
  };
};

export const requestPrivateChallengeAccess = async (
  challengeId: string,
  accessCode: string
): Promise<ChallengeJoinResult> => {
  const { data, error } = await supabase.rpc('request_private_challenge_access', {
    p_challenge_id: challengeId,
    p_access_code: accessCode.trim() || null
  });

  if (error) {
    logError('challenges', 'Falha ao pedir acesso ao challenge privado.', {
      challengeId,
      accessCode,
      error
    });
    return {
      ok: false,
      message: 'Não foi possível enviar seu pedido agora.'
    };
  }

  return (data as ChallengeJoinResult) || {
    ok: false,
    message: 'O challenge não respondeu ao pedido de acesso.'
  };
};

export const findPrivateChallengeByCode = async (
  accessCode: string
): Promise<{ challenge_id: string; title: string; description: string } | null> => {
  const { data, error } = await supabase.rpc('find_private_challenge_by_code', {
    p_access_code: accessCode.trim() || null
  });

  if (error) {
    logError('challenges', 'Falha ao procurar challenge privado por codigo.', {
      accessCode,
      error
    });
    return null;
  }

  const resultRow = Array.isArray(data) ? data[0] : null;
  return resultRow || null;
};

export const getChallengeJoinRequests = async (challengeId: string): Promise<ChallengeJoinRequest[]> => {
  const { data, error } = await supabase
    .from('challenge_join_requests')
    .select(`
      *,
      requester_profile:requester_user_id (
        id,
        username,
        display_name,
        avatar_url,
        aura_level,
        total_xp,
        streak,
        user_role
      ),
      invited_by_profile:invited_by_user_id (
        id,
        username,
        display_name,
        avatar_url,
        aura_level,
        total_xp,
        streak,
        user_role
      )
    `)
    .eq('challenge_id', challengeId)
    .order('created_at', { ascending: false });

  if (error) {
    logError('challenges', 'Falha ao buscar fila de pedidos do challenge privado.', {
      challengeId,
      error
    });
    return [];
  }

  return ((data as any[]) || []).map((row) => ({
    id: row.id,
    challenge_id: row.challenge_id,
    requester_user_id: row.requester_user_id,
    invited_by_user_id: row.invited_by_user_id || null,
    access_code: row.access_code || null,
    status: row.status,
    review_note: row.review_note || null,
    reviewed_by_user_id: row.reviewed_by_user_id || null,
    reviewed_at: row.reviewed_at || null,
    created_at: row.created_at,
    updated_at: row.updated_at,
    requester_profile: row.requester_profile ? buildFriendFromProfile(row.requester_profile) : undefined,
    invited_by_profile: row.invited_by_profile ? buildFriendFromProfile(row.invited_by_profile) : undefined
  }));
};

export const inviteFriendToPrivateChallenge = async (
  challengeId: string,
  friendUserId: string
): Promise<ChallengeJoinResult> => {
  const { data, error } = await supabase.rpc('invite_friend_to_private_challenge', {
    p_challenge_id: challengeId,
    p_friend_user_id: friendUserId
  });

  if (error) {
    logError('challenges', 'Falha ao convidar amigo para challenge privado.', {
      challengeId,
      friendUserId,
      error
    });
    return {
      ok: false,
      message: 'Não foi possível enviar o convite agora.'
    };
  }

  return (data as ChallengeJoinResult) || {
    ok: false,
    message: 'O convite não trouxe uma resposta válida.'
  };
};

export const reviewPrivateChallengeRequest = async (
  requestId: string,
  action: 'approve' | 'reject',
  reason?: string
): Promise<ChallengeJoinResult> => {
  const { data, error } = await supabase.rpc('review_private_challenge_request', {
    p_request_id: requestId,
    p_action: action,
    p_reason: reason?.trim() || null
  });

  if (error) {
    logError('challenges', 'Falha ao revisar pedido do challenge privado.', {
      requestId,
      action,
      error
    });
    return {
      ok: false,
      message: 'Não foi possível revisar esse pedido agora.'
    };
  }

  return (data as ChallengeJoinResult) || {
    ok: false,
    message: 'A revisão não trouxe uma resposta válida.'
  };
};

export const createChallenge = async (input: {
  title: string;
  description: string;
  visibility: 'public' | 'private';
  rules_text?: string | null;
  allow_late_join: boolean;
  entry_mode: 'free' | 'paid';
  entry_cost: number;
  duration_days: number;
  difficulty: 'Casual' | 'Hard Mode';
  created_by: string;
  starts_at?: string | null;
  ends_at?: string | null;
  is_perennial: boolean;
  tasks: Array<{
    title: string;
    description?: string | null;
    xp_reward?: number;
    category?: TaskCategory;
    frequency?: 'once' | 'daily' | 'weekly';
    target_completions?: number;
    requires_evidence?: boolean;
  }>;
}): Promise<string | null> => {
  const normalizedEntryMode = input.is_perennial ? 'free' : input.entry_mode;
  const normalizedEntryCost = normalizedEntryMode === 'paid' ? Math.max(50, Number(input.entry_cost) || 0) : 0;
  const normalizedDurationDays = input.is_perennial ? 0 : Math.max(1, Number(input.duration_days) || 1);
  const privateAccessToken = input.visibility === 'private'
    ? buildChallengePrivateAccessToken()
    : null;
  const privateAccessCode = input.visibility === 'private'
    ? buildChallengePrivateAccessCode()
    : null;

  const { data: challengeRow, error } = await supabase
    .from('challenges')
    .insert({
      title: input.title,
      description: input.description,
      visibility: input.visibility,
      rules_text: input.rules_text || null,
      private_access_token: privateAccessToken,
      private_access_code: privateAccessCode,
      allow_late_join: input.allow_late_join,
      entry_mode: normalizedEntryMode,
      entry_cost: normalizedEntryCost,
      duration_days: normalizedDurationDays,
      difficulty: input.difficulty,
      reward_xp: 0,
      pro_only: false,
      created_by: input.created_by,
      starts_at: input.starts_at,
      ends_at: input.ends_at,
      is_perennial: input.is_perennial
    })
    .select('id')
    .single();

  if (error || !challengeRow?.id) {
    console.error('Error creating challenge:', error);
    return null;
  }

  if (input.tasks.length) {
    const { error: tasksError } = await supabase
      .from('challenge_tasks')
      .insert(
        input.tasks.map((task, index) => ({
          challenge_id: challengeRow.id,
          title: task.title,
          description: task.description || null,
          category: task.category || 'Work',
          position: index,
          xp_reward: CHALLENGE_TASK_FIXED_XP,
          frequency: task.frequency || 'once',
          target_completions: Math.max(1, Number(task.target_completions) || 1),
          requires_evidence: Boolean(task.requires_evidence)
        }))
      );

    if (tasksError) {
      console.error('Error creating challenge tasks:', tasksError);
      return null;
    }
  }

  return challengeRow.id;
};

export const getChallengeAdminDraft = async (challengeId: string): Promise<ChallengeDetailData | null> => {
  const challengeDetail = await getChallengeDetail(challengeId, null);
  if (!challengeDetail) {
    return null;
  }

  return challengeDetail;
};

export const updateChallenge = async (
  challengeId: string,
  input: {
    title: string;
    description: string;
    visibility: 'public' | 'private';
    rules_text?: string | null;
    allow_late_join: boolean;
    entry_mode: 'free' | 'paid';
    entry_cost: number;
    duration_days: number;
    difficulty: 'Casual' | 'Hard Mode';
    starts_at?: string | null;
    ends_at?: string | null;
    is_perennial: boolean;
    tasks: Array<{
      id?: string;
      title: string;
      description?: string | null;
      xp_reward?: number;
      category?: TaskCategory;
      frequency?: 'once' | 'daily' | 'weekly';
      target_completions?: number;
      requires_evidence?: boolean;
    }>;
  }
): Promise<{ success: boolean; blockedTaskTitles?: string[] }> => {
  const normalizedEntryMode = input.is_perennial ? 'free' : input.entry_mode;
  const normalizedEntryCost = normalizedEntryMode === 'paid' ? Math.max(50, Number(input.entry_cost) || 0) : 0;
  const normalizedDurationDays = input.is_perennial ? 0 : Math.max(1, Number(input.duration_days) || 1);
  const { data: existingChallengeRow } = await supabase
    .from('challenges')
    .select('private_access_token, private_access_code')
    .eq('id', challengeId)
    .maybeSingle();

  const { data: existingTasks, error: existingTasksError } = await supabase
    .from('challenge_tasks')
    .select('id, title')
    .eq('challenge_id', challengeId);

  if (existingTasksError) {
    console.error('Error loading existing challenge tasks:', existingTasksError);
    return { success: false };
  }

  const existingTaskRows = (existingTasks as Array<{ id: string; title: string }>) || [];
  const existingTaskIds = existingTaskRows.map((task) => task.id);
  const incomingTaskIds = input.tasks
    .map((task) => task.id)
    .filter((taskId): taskId is string => Boolean(taskId));
  const taskIdsToDelete = existingTaskIds.filter((taskId) => !incomingTaskIds.includes(taskId));

  if (taskIdsToDelete.length) {
    const { data: completionRows, error: completionError } = await supabase
      .from('challenge_task_completions')
      .select('challenge_task_id')
      .in('challenge_task_id', taskIdsToDelete);

    if (completionError) {
      console.error('Error validating challenge task deletion:', completionError);
      return { success: false };
    }

    const blockedTaskIdSet = new Set(((completionRows as Array<{ challenge_task_id: string }>) || []).map((row) => row.challenge_task_id));
    if (blockedTaskIdSet.size > 0) {
      return {
        success: false,
        blockedTaskTitles: existingTaskRows
          .filter((task) => blockedTaskIdSet.has(task.id))
          .map((task) => task.title)
      };
    }
  }

  const { error: challengeError } = await supabase
    .from('challenges')
    .update({
      title: input.title,
      description: input.description,
      visibility: input.visibility,
      rules_text: input.rules_text || null,
      private_access_token: input.visibility === 'private'
        ? existingChallengeRow?.private_access_token || buildChallengePrivateAccessToken()
        : null,
      private_access_code: input.visibility === 'private'
        ? existingChallengeRow?.private_access_code || buildChallengePrivateAccessCode()
        : null,
      allow_late_join: input.allow_late_join,
      entry_mode: normalizedEntryMode,
      entry_cost: normalizedEntryCost,
      duration_days: normalizedDurationDays,
      difficulty: input.difficulty,
      reward_xp: 0,
      pro_only: false,
      starts_at: input.starts_at,
      ends_at: input.ends_at,
      is_perennial: input.is_perennial
    })
    .eq('id', challengeId);

  if (challengeError) {
    console.error('Error updating challenge:', challengeError);
    return { success: false };
  }

  if (taskIdsToDelete.length) {
    const { error: deleteError } = await supabase
      .from('challenge_tasks')
      .delete()
      .in('id', taskIdsToDelete);

    if (deleteError) {
      console.error('Error deleting removed challenge tasks:', deleteError);
      return { success: false };
    }
  }

  for (let index = 0; index < input.tasks.length; index += 1) {
    const task = input.tasks[index];
    const taskPayload = {
      challenge_id: challengeId,
      title: task.title,
      description: task.description || null,
      category: task.category || 'Work',
      position: index,
      xp_reward: CHALLENGE_TASK_FIXED_XP,
      frequency: task.frequency || 'once',
      target_completions: Math.max(1, Number(task.target_completions) || 1),
      requires_evidence: Boolean(task.requires_evidence)
    };

    if (task.id) {
      const { error: taskUpdateError } = await supabase
        .from('challenge_tasks')
        .update(taskPayload)
        .eq('id', task.id)
        .eq('challenge_id', challengeId);

      if (taskUpdateError) {
        console.error('Error updating challenge task:', taskUpdateError);
        return { success: false };
      }
    } else {
      const { error: taskInsertError } = await supabase
        .from('challenge_tasks')
        .insert(taskPayload);

      if (taskInsertError) {
        console.error('Error inserting new challenge task during update:', taskInsertError);
        return { success: false };
      }
    }
  }

  return { success: true };
};

export const getJoinedActiveChallenges = async (userId: string): Promise<ChallengeDetailData[]> => {
  // Comentario em pt-BR: essa leitura passou a usar RPC para nao depender
  // da policy da tabela logo depois do join e evitar sumico de challenge na Base.
  const { data: participants, error } = await supabase.rpc('get_my_joined_challenge_participants');

  if (error) {
    console.error('Error fetching joined challenges:', error);
    return [];
  }

  const participantRows = (participants as any[]) || [];
  const uniqueChallengeIds = [...new Set(
    participantRows
      .map((participant) => participant.challenge_id)
      .filter((challengeId): challengeId is string => typeof challengeId === 'string' && challengeId.length > 0)
  )];

  if (participantRows.length !== uniqueChallengeIds.length) {
    logWarn('challenges', 'Participacoes duplicadas detectadas na Base. Mantendo um challenge por id para evitar UI inconsistente.', {
      userId,
      participantRows: participantRows.length,
      uniqueChallenges: uniqueChallengeIds.length
    });
  }

  const detailResults = await Promise.all(
    uniqueChallengeIds.map((challengeId) => getChallengeDetail(challengeId, userId))
  );

  return detailResults
    .filter(Boolean)
    // Comentario em pt-BR: challenge encerrado continua visivel na arena,
    // mas nao deve seguir ocupando espaco na Base como se ainda estivesse ativo.
    .filter((detail) => detail!.challenge.status === 'active') as ChallengeDetailData[];
};

export const toggleChallengeTaskCompletion = async (
  participantId: string,
  challengeTaskId: string,
  isCompleted: boolean,
  evidenceFile?: File | null
): Promise<{ ok: boolean; message?: string }> => {
  const { data: challengeTask } = await supabase
    .from('challenge_tasks')
    .select('id, frequency, target_completions, requires_evidence, challenge_id')
    .eq('id', challengeTaskId)
    .maybeSingle();

  if (!challengeTask?.challenge_id) {
    console.error('Error loading challenge task before toggle:', challengeTaskId);
    return { ok: false, message: 'Nao consegui carregar os dados dessa tarefa do challenge.' };
  }

  const { data: myParticipantRows, error: myParticipantError } = await supabase.rpc('get_my_challenge_participant', {
    p_challenge_id: challengeTask.challenge_id
  });

  if (myParticipantError) {
    console.error('Error loading current user challenge participant via RPC:', myParticipantError);
    return { ok: false, message: 'Nao consegui validar sua participacao nesse challenge.' };
  }

  // Comentario em pt-BR: aqui a gente ignora participantId vindo da tela
  // se ele estiver velho e sempre pega a participacao ativa mais recente.
  const participantRows = ((myParticipantRows as any[]) || []).filter(
    (row) => row.status === 'active' || row.status === 'completed'
  );
  const participant = pickCurrentChallengeParticipant(participantRows);

  if (!participant?.id || !participant?.user_id) {
    console.error('Error resolving current user challenge participant before toggle:', {
      challengeTaskId,
      participantId,
      challengeId: challengeTask.challenge_id
    });
    return { ok: false, message: 'Sua participacao nesse challenge nao foi encontrada.' };
  }

  let completionRows: ChallengeTaskCompletion[] = [];
  const { data: completionRowsRpc, error: completionRowsRpcError } = await supabase.rpc('get_my_challenge_task_completions', {
    p_challenge_id: challengeTask.challenge_id
  });

  if (completionRowsRpcError) {
    logWarn('challenges', 'RPC de leitura das conclusoes do challenge indisponivel antes do toggle. Usando fallback direto.', {
      challengeId: challengeTask.challenge_id,
      participantId: participant.id,
      challengeTaskId,
      error: completionRowsRpcError
    });

    const { data: completions } = await supabase
      .from('challenge_task_completions')
      .select('*')
      .eq('participant_id', participant.id)
      .eq('challenge_task_id', challengeTaskId)
      .order('completion_date', { ascending: false })
      .order('completed_at', { ascending: false });

    completionRows = ((completions as ChallengeTaskCompletion[]) || []).filter(
      (completion) => completion.evidence_status !== 'rejected'
    );
  } else {
    completionRows = ((completionRowsRpc as ChallengeTaskCompletion[]) || [])
      .filter((completion) => completion.participant_id === participant.id)
      .filter((completion) => completion.challenge_task_id === challengeTaskId)
      .filter((completion) => completion.evidence_status !== 'rejected');
  }

  // Comentario em pt-BR: conclusao rejeitada nao pode travar o botao
  // nem contar como progresso valido no check da Home.
  const progress = getChallengeTaskProgress(challengeTask as ChallengeTask, completionRows);
  const currentCycleCompletion = challengeTask.frequency === 'once'
    ? completionRows[completionRows.length - 1]
    : completionRows.find((completion) => completion.completion_date === progress.currentCycleKey);
  const completionDate = currentCycleCompletion?.completion_date
    || (challengeTask.frequency === 'once' ? getTodayLocalDateOnly() : progress.currentCycleKey)
    || getTodayLocalDateOnly();
  let uploadedEvidencePath: string | null = null;
  let evidenceCapturedAt: string | null = null;

  if (isCompleted) {
    if (!currentCycleCompletion?.id) {
      return { ok: true };
    }
  } else {
    if (progress.isFullyCompleted || progress.isCompletedInCurrentCycle) {
      return { ok: true };
    }

    if (challengeTask.requires_evidence && !evidenceFile) {
      console.error('Error creating challenge completion without evidence:', challengeTaskId);
      return { ok: false, message: 'Essa tarefa exige evidencia para registrar o check.' };
    }

    if (evidenceFile) {
      const extractedMetadata = await extractImageMetadata(evidenceFile);
      evidenceCapturedAt = extractedMetadata.capturedAt;
      const uploadedEvidence = await uploadPrivateImage(TASK_EVIDENCE_BUCKET, participant.user_id, 'challenge-evidence', evidenceFile);
      if (!uploadedEvidence?.path) {
        return { ok: false, message: 'Nao foi possivel enviar a evidencia dessa tarefa.' };
      }
      uploadedEvidencePath = uploadedEvidence.path;
    }
  }

  const { data, error } = await supabase.rpc('toggle_my_challenge_task_completion', {
    p_challenge_task_id: challengeTaskId,
    p_mark_complete: !isCompleted,
    p_completion_date: completionDate,
    p_evidence_storage_path: uploadedEvidencePath,
    p_evidence_captured_at: evidenceCapturedAt
  });

  if (error || !(data as { ok?: boolean } | null)?.ok) {
    console.error('Error toggling challenge task completion via RPC:', error || data);
    return {
      ok: false,
      message: ((data as { message?: string } | null)?.message) || 'Nao foi possivel registrar essa tarefa do desafio agora.'
    };
  }

  return { ok: true };
};

export const getChallengeLeaderboard = async (challengeId: string): Promise<ChallengeLeaderboardEntry[]> => {
  const { data: challengeTaskRows } = await supabase
    .from('challenge_tasks')
    .select('target_completions')
    .eq('challenge_id', challengeId);

  const totalExpectedCompletions = ((challengeTaskRows as Array<{ target_completions?: number }> | null) || [])
    .reduce((sum, task) => sum + Math.max(Number(task.target_completions || 1), 1), 0);

  const { data: snapshotRows, error } = await supabase
    .rpc('get_challenge_leaderboard_snapshot', { p_challenge_id: challengeId });

  if (!error) {
    const mappedSnapshotRows = normalizeChallengeLeaderboardEntries(((snapshotRows as any[]) || []).map((row) => ({
      participant_id: row.participant_id,
      challenge_id: row.challenge_id,
      user_id: row.user_id,
      username: row.username || 'player',
      display_name: row.display_name || row.username || 'Player',
      avatar_url: row.avatar_url || undefined,
      user_role: row.user_role || 'free',
      score_xp: Number(row.score_xp || 0),
      valid_completions: Number(row.valid_completions || 0),
      rejected_completions: Number(row.rejected_completions || 0),
      progress_percent: Number(row.progress_percent || 0),
      rank: Number(row.rank || 0),
      participant_status: row.participant_status,
      evidences_count: Number(row.evidences_count || 0),
      perfect_days: Number(row.perfect_days || 0),
      reported_evidences_count: Number(row.reported_evidences_count || 0),
      pending_review_count: Number(row.pending_review_count || 0),
      highest_report_percent: Number(row.highest_report_percent || 0)
    })) as ChallengeLeaderboardEntry[], totalExpectedCompletions);

    // Comentario em pt-BR: se o snapshot vier todo zerado mesmo com progresso salvo,
    // a gente desconfia da RPC e recalcula pelo feed das conclusoes para nao travar o ranking.
    const snapshotLooksBroken = mappedSnapshotRows.length > 0
      && mappedSnapshotRows.every((entry) => entry.score_xp === 0 && entry.valid_completions === 0)
      && mappedSnapshotRows.some((entry) => entry.progress_percent > 0);

    if (!snapshotLooksBroken) {
      return sortChallengeLeaderboardEntries(mappedSnapshotRows);
    }

    logWarn('challenge-ranking', 'Snapshot do leaderboard veio zerado apesar de existir progresso. Vou recalcular no fallback.', {
      challengeId,
      participants: mappedSnapshotRows.length
    });
  }

  if (error) {
    console.error('Error fetching challenge leaderboard snapshot via rpc:', error);
  }

  let participantRows: any[] = [];
  const { data: rosterRows, error: rosterError } = await supabase
    .rpc('get_challenge_participant_roster', { p_challenge_id: challengeId });

  if (rosterError) {
    console.error('Error fetching challenge leaderboard participants via rpc:', rosterError);
    return [];
  }

  participantRows = ((rosterRows as any[]) || []).map((row) => ({
    id: row.id,
    challenge_id: row.challenge_id,
    user_id: row.user_id,
    status: row.status,
    progress_percent: row.progress_percent,
    joined_at: row.joined_at,
    created_at: row.created_at,
    profiles: {
      username: row.username,
      display_name: row.display_name,
      avatar_url: row.avatar_url,
      user_role: row.user_role
    }
  }));
  const { data: challengeRow } = await supabase
    .from('challenges')
    .select('id, entry_mode, ends_at, payout_completed_at')
    .eq('id', challengeId)
    .maybeSingle();

  if (challengeRow) {
    await settleChallengeCreditPoolIfNeeded(mapChallengeRow(challengeRow, 0));
  }

  if (!participantRows.length) {
    return [];
  }

  const participantIds = participantRows.map((participant) => participant.id);
  let completionRows: any[] = [];
  const { data: completionFeedRows, error: completionFeedError } = await supabase
    .rpc('get_challenge_completion_score_feed', { p_challenge_id: challengeId });

  if (completionFeedError) {
    console.error('Error fetching challenge completion score feed via rpc:', completionFeedError);

    const { data: completionsFallback } = await supabase
      .from('challenge_task_completions')
      .select('id, participant_id, challenge_task_id, evidence_storage_path, evidence_status, report_reviewed_at, completed_at, completion_date')
      .in('participant_id', participantIds);

    completionRows = (completionsFallback as any[]) || [];
  } else {
    completionRows = (completionFeedRows as any[]) || [];
  }

  const { data: tasks } = await supabase
    .from('challenge_tasks')
    .select('id, xp_reward')
    .eq('challenge_id', challengeId);

  const completionIds = (completionRows.map((completion) => completion.id)).filter(Boolean);
  const { data: reports } = completionIds.length
    ? await supabase
        .from('challenge_completion_reports')
        .select('completion_id, reporter_user_id')
        .in('completion_id', completionIds)
    : { data: [] as any[] };

  const statsByParticipant = buildChallengeScoreSnapshots(
    participantRows,
    completionRows,
    (tasks as any[]) || [],
    (reports as any[]) || []
  );

  return sortChallengeLeaderboardEntries(normalizeChallengeLeaderboardEntries(participantRows
    .map((participant) => {
      const stats = statsByParticipant.get(participant.id) || createEmptyChallengeScoreSnapshot();

      return {
        participant_id: participant.id,
        challenge_id: participant.challenge_id,
        user_id: participant.user_id,
        username: participant.profiles?.username || 'player',
        display_name: participant.profiles?.display_name || participant.profiles?.username || 'Player',
        avatar_url: participant.profiles?.avatar_url || undefined,
        user_role: participant.profiles?.user_role || 'free',
        score_xp: stats.score_xp,
        valid_completions: stats.valid_completions,
        rejected_completions: stats.rejected_completions,
        progress_percent: participant.progress_percent || 0,
        rank: 0,
        participant_status: participant.status,
        evidences_count: stats.evidences_count,
        perfect_days: stats.perfect_days,
        reported_evidences_count: stats.reported_evidences_count,
        pending_review_count: stats.pending_review_count,
        highest_report_percent: stats.highest_report_percent
      } satisfies ChallengeLeaderboardEntry;
    }), totalExpectedCompletions));
};

export const getChallengeEvidenceFeed = async (
  challengeId: string,
  participantId: string,
  viewerUserId?: string | null
): Promise<ChallengeEvidenceItem[]> => {
  const [completionRows, rosterRowsResponse] = await Promise.all([
    getChallengeParticipantCompletionFeed(challengeId, participantId),
    supabase.rpc('get_challenge_participant_roster', { p_challenge_id: challengeId })
  ]);
  const completionRowsWithEvidence = completionRows.filter((completion) => completion.evidence_storage_path);
  const completionIds = completionRows.map((completion) => completion.id);
  const { data: reports } = completionIds.length
    ? await supabase.from('challenge_completion_reports').select('completion_id, reporter_user_id').in('completion_id', completionIds)
    : { data: [] as any[] };
  const rosterRows = (rosterRowsResponse.data as any[]) || [];
  const participantCount = rosterRows.length;
  const participantProfile = rosterRows.find((row) => row.id === participantId);

  const reportMap = new Map<string, string[]>();
  ((reports as any[]) || []).forEach((report) => {
    const current = reportMap.get(report.completion_id) || [];
    current.push(report.reporter_user_id);
    reportMap.set(report.completion_id, current);
  });

  return Promise.all(completionRowsWithEvidence.map(async (completion) => {
    const reportUserIds = reportMap.get(completion.id) || [];
    const eligibleReviewers = Math.max(1, (participantCount || 1) - 1);
    const reportPercent = Math.round((reportUserIds.length / eligibleReviewers) * 100);

    return {
      ...(await resolveChallengeCompletionEvidence(completion as ChallengeTaskCompletion)),
      task_title: completion.task_title || 'Tarefa',
      participant_display_name: participantProfile?.display_name || participantProfile?.username || 'Player',
      participant_avatar_url: participantProfile?.avatar_url || undefined,
      participant_user_role: participantProfile?.user_role || 'free',
      requires_evidence: Boolean(completion.task_requires_evidence),
      report_count: reportUserIds.length,
      report_percent: reportPercent,
      reporter_has_reported: viewerUserId ? reportUserIds.includes(viewerUserId) : false,
      evidence_date_mismatch: didEvidenceDateMismatch(completion.evidence_captured_at, completion.completed_at)
    } satisfies ChallengeEvidenceItem;
  }));
};

export const getChallengeParticipantActivity = async (
  challengeId: string,
  participantId: string
): Promise<ChallengeParticipantActivityDay[]> => {
  const [{ data: tasks, error: tasksError }, completions] = await Promise.all([
    supabase
      .from('challenge_tasks')
      .select('id, title, frequency, target_completions')
      .eq('challenge_id', challengeId),
    getChallengeParticipantCompletionFeed(challengeId, participantId)
  ]);

  if (tasksError) {
    logError('challenge-detail', 'Falha ao buscar tarefas para o calendario do challenge.', {
      challengeId,
      participantId,
      error: tasksError
    });
  }

  const taskRows = (tasks as Array<{ id: string; title?: string | null; frequency?: string | null; target_completions?: number | null }>) || [];
  const dailyTaskRows = taskRows.filter((task) => task.frequency === 'daily');
  const weeklyTaskRows = taskRows.filter((task) => task.frequency === 'weekly');
  const trackedTaskRows = dailyTaskRows.length > 0
    ? dailyTaskRows
    : weeklyTaskRows.length > 0
      ? weeklyTaskRows
      : taskRows.filter((task) => task.frequency !== 'once');
  const trackedTaskKeys = new Set(
    trackedTaskRows
      .map((task) => normalizeChallengeTaskKey(task.id, task.title))
      .filter((value): value is string => Boolean(value))
  );
  const targetByTaskKey = new Map(
    trackedTaskRows
      .map((task) => [normalizeChallengeTaskKey(task.id, task.title), Math.max(Number(task.target_completions || 1), 1)] as const)
      .filter((entry): entry is readonly [string, number] => Boolean(entry[0]))
  );
  const titleByTaskKey = new Map(
    trackedTaskRows
      .map((task) => [normalizeChallengeTaskKey(task.id, task.title), task.title || 'Tarefa'] as const)
      .filter((entry): entry is readonly [string, string] => Boolean(entry[0]))
  );
  const totalTasksInChallenge = Math.max(trackedTaskKeys.size, 1);
  const totalExpectedCompletionsInDay = Math.max(
    trackedTaskRows.reduce((sum, task) => sum + Math.max(Number(task.target_completions || 1), 1), 0),
    1
  );
  const daysMap = new Map<string, { completionCountByTask: Map<string, number>; taskTitles: Set<string>; validCompletions: number }>();
  const validCompletions = (completions || []).filter((completion) => completion.evidence_status !== 'rejected');
  const validCompletionDates = [...new Set(
    validCompletions
      .map((completion) => completion.completion_date || getLocalDateOnlyFromValue(completion.completed_at))
      .filter((value): value is string => Boolean(value))
  )].sort();

  validCompletions.forEach((completion) => {
      const date = completion.completion_date || getLocalDateOnlyFromValue(completion.completed_at);
      if (!date) {
        return;
      }

      const current = daysMap.get(date) || {
        completionCountByTask: new Map<string, number>(),
        taskTitles: new Set<string>(),
        validCompletions: 0
      };

      const taskKey = normalizeChallengeTaskKey(completion.challenge_task_id, completion.task_title);
      if (taskKey && trackedTaskKeys.has(taskKey)) {
        current.validCompletions += 1;
        current.completionCountByTask.set(
          taskKey,
          (current.completionCountByTask.get(taskKey) || 0) + 1
        );
      }

      daysMap.set(date, current);
    });

  return validCompletionDates
    .map((date) => {
      const stats = daysMap.get(date) || {
        completionCountByTask: new Map<string, number>(),
        taskTitles: new Set<string>(),
        validCompletions: 0
      };
      const taskIdsWithExecutionToday = new Set(stats.completionCountByTask.keys());
      const satisfiedTaskIds = trackedTaskRows
        .map((task) => {
          const taskKey = normalizeChallengeTaskKey(task.id, task.title);
          if (!taskKey) {
            return null;
          }

          const hasExecutionToday = taskIdsWithExecutionToday.has(taskKey);
          if (hasExecutionToday) {
            return taskKey;
          }

          const completionCountUntilDate = validCompletions.reduce((total, completion) => {
            const completionTaskKey = normalizeChallengeTaskKey(completion.challenge_task_id, completion.task_title);
            const completionDate = completion.completion_date || getLocalDateOnlyFromValue(completion.completed_at);
            if (completionTaskKey !== taskKey || !completionDate || completionDate > date) {
              return total;
            }
            return total + 1;
          }, 0);

          return completionCountUntilDate >= (targetByTaskKey.get(taskKey) || 1) ? taskKey : null;
        })
        .filter((value): value is string => Boolean(value));

      satisfiedTaskIds.forEach((taskKey) => {
        stats.taskTitles.add(titleByTaskKey.get(taskKey) || 'Tarefa');
      });

      return {
        date,
        valid_completions: stats.validCompletions,
        tasks_with_execution: satisfiedTaskIds.length,
        unique_tasks_completed: satisfiedTaskIds.length,
        total_tasks_in_challenge: totalTasksInChallenge,
        expected_completions_in_day: totalExpectedCompletionsInDay,
        // No calendario, "dia total" precisa bater com o que a pessoa enxerga no card:
        // tarefa já fechada no total continua contando como satisfeita nos dias seguintes.
        completion_state: satisfiedTaskIds.length >= totalTasksInChallenge
          ? 'full'
          : 'partial',
        task_titles: Array.from(stats.taskTitles)
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
};

export const getChallengeParticipantDayEntries = async (
  challengeId: string,
  participantId: string,
  date: string,
  viewerUserId?: string | null
): Promise<ChallengeParticipantDayEntry[]> => {
  const [completionRowsAll, rosterRowsResponse, tasksResponse] = await Promise.all([
    getChallengeParticipantCompletionFeed(challengeId, participantId),
    supabase.rpc('get_challenge_participant_roster', { p_challenge_id: challengeId }),
    supabase
      .from('challenge_tasks')
      .select('id, title, description, frequency, target_completions, requires_evidence, xp_reward')
      .eq('challenge_id', challengeId)
  ]);
  const completionRowsAllValid = completionRowsAll.filter((completion) => completion.evidence_status !== 'rejected');
  const taskRows = (tasksResponse.data as Array<{
    id: string;
    title?: string | null;
    description?: string | null;
    frequency?: string | null;
    target_completions?: number | null;
    requires_evidence?: boolean | null;
    xp_reward?: number | null;
  }> | null) || [];
  const dailyTaskRows = taskRows.filter((task) => task.frequency === 'daily');
  const weeklyTaskRows = taskRows.filter((task) => task.frequency === 'weekly');
  const trackedTaskRows = dailyTaskRows.length > 0
    ? dailyTaskRows
    : weeklyTaskRows.length > 0
      ? weeklyTaskRows
      : taskRows.filter((task) => task.frequency !== 'once');
  const targetByTaskKey = new Map(
    trackedTaskRows
      .map((task) => [normalizeChallengeTaskKey(task.id, task.title), Math.max(Number(task.target_completions || 1), 1)] as const)
      .filter((entry): entry is readonly [string, number] => Boolean(entry[0]))
  );
  const taskByKey = new Map(
    trackedTaskRows
      .map((task) => [normalizeChallengeTaskKey(task.id, task.title), task] as const)
      .filter((entry): entry is readonly [string, typeof taskRows[number]] => Boolean(entry[0]))
  );
  const completionRows = trackedTaskRows.flatMap((task) => {
    const taskKey = normalizeChallengeTaskKey(task.id, task.title);
    if (!taskKey) {
      return [];
    }

    const relevantCompletions = completionRowsAllValid
      .filter((completion) => normalizeChallengeTaskKey(completion.challenge_task_id, completion.task_title) === taskKey)
      .filter((completion) => {
        const completionDate = completion.completion_date || getLocalDateOnlyFromValue(completion.completed_at);
        return Boolean(completionDate) && completionDate! <= date;
      })
      .sort((a, b) => new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime());

    if (!relevantCompletions.length) {
      return [];
    }

    const dayCompletions = relevantCompletions.filter((completion) => {
      const completionDate = completion.completion_date || getLocalDateOnlyFromValue(completion.completed_at);
      return completionDate === date;
    });

    if (dayCompletions.length > 0) {
      return [dayCompletions[0]];
    }

    const target = targetByTaskKey.get(taskKey) || 1;
    if (relevantCompletions.length < target) {
      return [];
    }

    const completionWithEvidence = relevantCompletions.find((completion) => completion.evidence_storage_path || completion.evidence_url);
    const carriedCompletion = completionWithEvidence || relevantCompletions[0];

    return [{
      ...carriedCompletion,
      completed_at: carriedCompletion.completed_at,
      completion_date: carriedCompletion.completion_date,
      report_review_note: carriedCompletion.report_review_note,
      task_title: carriedCompletion.task_title || task.title || 'Tarefa',
      task_description: carriedCompletion.task_description || task.description || null,
      task_requires_evidence: Boolean(carriedCompletion.task_requires_evidence || task.requires_evidence),
      task_xp_reward: Number(carriedCompletion.task_xp_reward || task.xp_reward || CHALLENGE_TASK_FIXED_XP),
      metadata: {
        is_carried_completion: true,
        source_completion_date: carriedCompletion.completion_date || getLocalDateOnlyFromValue(carriedCompletion.completed_at)
      }
    } as ChallengeParticipantCompletionFeedRow];
  });
  const completionIds = completionRows.map((completion) => completion.id).filter(Boolean);
  const { data: reports } = completionIds.length
    ? await supabase
        .from('challenge_completion_reports')
        .select('completion_id, reporter_user_id')
        .in('completion_id', completionIds)
    : { data: [] as any[] };
  const rosterRows = (rosterRowsResponse.data as any[]) || [];
  const participantCount = rosterRows.length;

  const reportMap = new Map<string, string[]>();
  ((reports as any[]) || []).forEach((report) => {
    const current = reportMap.get(report.completion_id) || [];
    current.push(report.reporter_user_id);
    reportMap.set(report.completion_id, current);
  });

  return Promise.all(completionRows.map(async (completion) => {
    const reportUserIds = reportMap.get(completion.id) || [];
    const eligibleReviewers = Math.max(1, (participantCount || 1) - 1);
    const reportPercent = Math.round((reportUserIds.length / eligibleReviewers) * 100);

    return {
      ...(await resolveChallengeCompletionEvidence(completion as ChallengeTaskCompletion)),
      task_title: completion.task_title || 'Tarefa',
      task_description: completion.task_description || null,
      xp_reward: Number(completion.task_xp_reward || CHALLENGE_TASK_FIXED_XP),
      requires_evidence: Boolean(completion.task_requires_evidence),
      report_count: reportUserIds.length,
      report_percent: reportPercent,
      reporter_has_reported: viewerUserId ? reportUserIds.includes(viewerUserId) : false,
      evidence_date_mismatch: didEvidenceDateMismatch(completion.evidence_captured_at, completion.completed_at),
      is_carried_completion: Boolean((completion as ChallengeParticipantCompletionFeedRow & { metadata?: Record<string, unknown> }).metadata?.is_carried_completion),
      source_completion_date: ((completion as ChallengeParticipantCompletionFeedRow & { metadata?: Record<string, unknown> }).metadata?.source_completion_date as string | undefined) || completion.completion_date || getLocalDateOnlyFromValue(completion.completed_at)
    } satisfies ChallengeParticipantDayEntry;
  }));
};

export const reviewChallengeEvidence = async (
  completionId: string,
  action: 'reject' | 'keep',
  reason?: string
): Promise<ChallengeEvidenceReviewResult> => {
  const { data, error } = await supabase.rpc('review_challenge_evidence', {
    p_completion_id: completionId,
    p_action: action,
    p_reason: reason?.trim() || null
  });

  if (error) {
    logError('challenge-detail', 'Falha ao revisar a evidencia do challenge.', {
      completionId,
      action,
      error
    });
    return {
      ok: false,
      message: 'Não foi possível revisar essa prova agora.'
    };
  }

  return (data as ChallengeEvidenceReviewResult) || {
    ok: false,
    message: 'A moderação não trouxe uma resposta válida.'
  };
};

export const submitChallengeCompletionReport = async (
  challengeId: string,
  completionId: string,
  reporterUserId: string,
  reason: string
): Promise<{ success: boolean; reportPercent: number; participantBanned: boolean }> => {
  const { error } = await supabase
    .from('challenge_completion_reports')
    .insert({
      challenge_id: challengeId,
      completion_id: completionId,
      reporter_user_id: reporterUserId,
      reason: reason.trim()
    });

  if (error) {
    console.error('Error creating challenge report:', error);
    return { success: false, reportPercent: 0, participantBanned: false };
  }

  const [{ data: reports }, { count: participantCount }, { data: completion }] = await Promise.all([
    supabase.from('challenge_completion_reports').select('id').eq('completion_id', completionId),
    supabase.from('challenge_participants').select('*', { count: 'exact', head: true }).eq('challenge_id', challengeId),
    supabase.from('challenge_task_completions').select('id, participant_id').eq('id', completionId).maybeSingle()
  ]);

  const reportCount = ((reports as any[]) || []).length;
  const eligibleReviewers = Math.max(1, (participantCount || 1) - 1);
  const reportPercent = Math.round((reportCount / eligibleReviewers) * 100);

  let participantBanned = false;

  if (reportCount >= 2 && reportPercent >= 30 && completion?.participant_id) {
    await supabase
      .from('challenge_task_completions')
      .update({ evidence_status: 'rejected' })
      .eq('id', completionId);

    const { count: rejectedCount } = await supabase
      .from('challenge_task_completions')
      .select('*', { count: 'exact', head: true })
      .eq('participant_id', completion.participant_id)
      .eq('evidence_status', 'rejected');

    if ((rejectedCount || 0) >= 3) {
      participantBanned = true;
      await supabase
        .from('challenge_participants')
        .update({
          status: 'failed',
          result_message: 'Participacao encerrada por repetidas evidencias reprovadas pela comunidade.'
        })
        .eq('id', completion.participant_id);
    }

    await syncChallengeParticipantProgress(completion.participant_id);
  }

  return { success: true, reportPercent, participantBanned };
};

// XP History operations
export const getXpHistory = async (userId: string): Promise<LedgerEntry[]> => {
  const { data, error } = await supabase
    .from('xp_history')
    .select('*')
    .eq('user_id', userId)
    .order('date', { ascending: false });

  if (error) {
    console.error('Error fetching XP history:', error);
    return [];
  }

  return data?.map(entry => ({
    id: entry.id,
    source: entry.source,
    amount: entry.amount,
    date: entry.date,
    type: entry.amount > 0 ? 'gain' : 'loss'
  })) || [];
};

export const addXpEntry = async (userId: string, source: string, amount: number): Promise<boolean> => {
  const { error } = await supabase
    .from('xp_history')
    .insert({ user_id: userId, source, amount });

  if (error) {
    console.error('Error adding XP entry:', error);
    return false;
  }

  return true;
};

// Badge operations
export const getBadges = async (userId: string, options?: { includeLocked?: boolean }): Promise<Badge[]> => {
  return getBadgesForProfile(userId, options);
};

export const unlockBadge = async (userId: string, badge: Omit<Badge, 'id' | 'user_id' | 'unlocked_at'>): Promise<boolean> => {
  const { error } = await supabase
    .from('badges')
    .insert({
      user_id: userId,
      ...badge,
      unlocked_at: new Date().toISOString()
    });

  if (error) {
    console.error('Error unlocking badge:', error);
    return false;
  }

  return true;
};

// Day Log operations
const DAY_LOG_RECONCILIATION_STORAGE_PREFIX = 'dopamind_day_log_reconciled_';

const getDayLogReconciliationStorageKey = (userId: string) => `${DAY_LOG_RECONCILIATION_STORAGE_PREFIX}${userId}`;

const shouldRunDayLogReconciliation = (userId: string): boolean => {
  if (typeof window === 'undefined') {
    return true;
  }

  const today = getTodayLocalDateOnly();
  return window.sessionStorage.getItem(getDayLogReconciliationStorageKey(userId)) !== today;
};

const markDayLogReconciliationRun = (userId: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(getDayLogReconciliationStorageKey(userId), getTodayLocalDateOnly());
};

export const invalidateDayLogReconciliation = (userId: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  // Comentario em pt-BR: sempre que uma conclusao muda o dia atual,
  // a gente derruba o cache da reconciliacao para o calendario reler o status certo.
  window.sessionStorage.removeItem(getDayLogReconciliationStorageKey(userId));
};

const reconcileRecentDayLogsFromTaskCompletions = async (userId: string): Promise<void> => {
  if (!shouldRunDayLogReconciliation(userId)) {
    return;
  }

  try {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 14);

    const [{ data: completions, error: completionsError }, { data: currentLogs, error: logsError }] = await Promise.all([
      supabase
        .from('task_completions')
        .select('completed_at, xp_earned, metadata')
        .eq('user_id', userId)
        .gte('completed_at', cutoff.toISOString())
        .order('completed_at', { ascending: false }),
      supabase
        .from('day_logs')
        .select('*')
        .eq('user_id', userId)
        .gte('date', toLocalDateOnly(cutoff))
        .order('date', { ascending: false })
    ]);

    if (completionsError) {
      logError('day-logs', 'Falha ao buscar task_completions para reconciliar day_logs.', completionsError);
      return;
    }

    if (logsError) {
      logError('day-logs', 'Falha ao buscar day_logs para reconciliar datas recentes.', logsError);
      return;
    }

    const completionRows = (completions as Array<{ completed_at: string; xp_earned: number; metadata?: Record<string, unknown> | null }>) || [];
    const dayLogs = (currentLogs as DayLog[]) || [];

    if (!completionRows.length) {
      markDayLogReconciliationRun(userId);
      return;
    }

    const completionSummaryByDate = completionRows.reduce<Map<string, { xp: number; hasPerfectTrigger: boolean }>>((acc, completion) => {
      const localDate = getLocalDateOnlyFromValue(completion.completed_at);
      if (!localDate) {
        return acc;
      }

      const current = acc.get(localDate) || { xp: 0, hasPerfectTrigger: false };
      acc.set(localDate, {
        xp: current.xp + Number(completion.xp_earned || 0),
        hasPerfectTrigger: current.hasPerfectTrigger || Boolean(completion.metadata?.perfect_day_triggered)
      });
      return acc;
    }, new Map<string, { xp: number; hasPerfectTrigger: boolean }>());

    const dayLogMap = new Map(dayLogs.map((log) => [log.date, log]));
    let movedLogs = 0;
    let createdLogs = 0;
    let updatedLogs = 0;

    for (const [localDate, summary] of completionSummaryByDate.entries()) {
      const totalXp = summary.xp;
      const sameDayLog = dayLogMap.get(localDate);
      const nextDate = toLocalDateOnly(addDays(new Date(`${localDate}T00:00:00`), 1));
      const nextDayLog = dayLogMap.get(nextDate);
      const nextDayHasOwnCompletions = completionSummaryByDate.has(nextDate);

      if (!sameDayLog && nextDayLog && !nextDayHasOwnCompletions && Number(nextDayLog.xp_earned || 0) === totalXp) {
        const { error: deleteError } = await supabase
          .from('day_logs')
          .delete()
          .eq('user_id', userId)
          .eq('date', nextDate);

        if (deleteError) {
          logError('day-logs', 'Falha ao mover um day_log UTC para o dia local correto.', deleteError);
          continue;
        }

        const shiftedLog: DayLog = {
          date: localDate,
          status: nextDayLog.status,
          xp_earned: nextDayLog.xp_earned
        };

        const { error: insertShiftedError } = await supabase
          .from('day_logs')
          .upsert({
            user_id: userId,
            date: shiftedLog.date,
            status: shiftedLog.status,
            xp_earned: shiftedLog.xp_earned
          });

        if (insertShiftedError) {
          logError('day-logs', 'Falha ao recriar o day_log corrigido no dia local.', insertShiftedError);
          continue;
        }

        dayLogMap.delete(nextDate);
        dayLogMap.set(localDate, shiftedLog);
        movedLogs += 1;
        continue;
      }

      if (!sameDayLog) {
        const { error: createError } = await supabase
          .from('day_logs')
          .upsert({
            user_id: userId,
            date: localDate,
            status: summary.hasPerfectTrigger ? 'perfect' : 'active',
            xp_earned: totalXp
          });

        if (createError) {
          logError('day-logs', 'Falha ao criar day_log ausente a partir de task_completions.', createError);
          continue;
        }

        dayLogMap.set(localDate, {
          date: localDate,
          status: summary.hasPerfectTrigger ? 'perfect' : 'active',
          xp_earned: totalXp
        });
        createdLogs += 1;
        continue;
      }

      const shouldPromoteStatus = sameDayLog.status === 'inactive'
        || (sameDayLog.status !== 'perfect' && summary.hasPerfectTrigger);
      const shouldRaiseXp = Number(sameDayLog.xp_earned || 0) < totalXp;

      if (shouldPromoteStatus || shouldRaiseXp) {
        const { error: updateError } = await supabase
          .from('day_logs')
          .upsert({
            user_id: userId,
            date: localDate,
            status: sameDayLog.status === 'perfect' || summary.hasPerfectTrigger ? 'perfect' : 'active',
            xp_earned: Math.max(Number(sameDayLog.xp_earned || 0), totalXp)
          });

        if (updateError) {
          logError('day-logs', 'Falha ao reforcar um day_log existente com dados reais de conclusao.', updateError);
          continue;
        }

        updatedLogs += 1;
      }
    }

    markDayLogReconciliationRun(userId);

    if (movedLogs || createdLogs || updatedLogs) {
      logInfo('day-logs', 'Reconciliacao automatica de day_logs executada.', {
        userId,
        movedLogs,
        createdLogs,
        updatedLogs
      });
    }
  } catch (error) {
    logError('day-logs', 'Falha inesperada ao reconciliar day_logs recentes.', error);
  }
};

export const getDayLogs = async (userId: string): Promise<DayLog[]> => {
  await reconcileRecentDayLogsFromTaskCompletions(userId);

  const { data, error } = await supabase
    .from('day_logs')
    .select('*')
    .eq('user_id', userId)
    .order('date', { ascending: false });

  if (error) {
    console.error('Error fetching day logs:', error);
    return [];
  }

  const dayLogs = ((data as DayLog[]) || []);
  const mergedDayLogs = mergeDayLogsByDate(dayLogs);

  if (mergedDayLogs.length !== dayLogs.length) {
    logInfo('day-logs', 'Duplicidades de day_logs consolidadas na leitura.', {
      userId,
      originalRows: dayLogs.length,
      mergedRows: mergedDayLogs.length
    });
  }

  return mergedDayLogs;
};

export const getConsistencyStats = async (userId: string): Promise<ConsistencyStats> => {
  const dayLogs = await getDayLogs(userId);
  return calculateConsistencyStatsFromLogs(dayLogs);
};

export const syncProfileStreakFromDayLogs = async (userId: string): Promise<ConsistencyStats | null> => {
  const [profile, consistencyStats] = await Promise.all([
    getProfile(userId),
    getConsistencyStats(userId)
  ]);

  if (!profile) {
    return null;
  }

  if ((profile.streak || 0) !== consistencyStats.current_streak) {
    await updateProfile(userId, {
      streak: consistencyStats.current_streak
    });
  }

  return consistencyStats;
};

export const updateDayLog = async (userId: string, date: string, status: string, xpEarned: number): Promise<boolean> => {
  const { data: existingLogs, error: fetchError } = await supabase
    .from('day_logs')
    .select('date, status, xp_earned')
    .eq('user_id', userId)
    .eq('date', date);

  if (fetchError) {
    console.error('Error fetching existing day log:', fetchError);
    return false;
  }

  const mergedExistingLog = mergeDayLogsByDate(((existingLogs as DayLog[]) || [])).find((log) => log.date === date);
  const nextStatus = mergedExistingLog?.status === 'perfect' || status === 'perfect'
    ? 'perfect'
    : status;

  const nextDate = toLocalDateOnly(addDays(new Date(`${date}T00:00:00`), 1));
  const { data: dayCompletions, error: completionsError } = await supabase
    .from('task_completions')
    .select('xp_earned')
    .eq('user_id', userId)
    .gte('completed_at', `${date}T00:00:00`)
    .lt('completed_at', `${nextDate}T00:00:00`);

  if (completionsError) {
    console.error('Error fetching task completions for day log snapshot:', completionsError);
    return false;
  }

  const completionXpTotal = ((dayCompletions as Array<{ xp_earned: number }>) || []).reduce((total, completion) => {
    return total + Number(completion.xp_earned || 0);
  }, 0);
  const nextXp = Math.max(completionXpTotal, (mergedExistingLog?.xp_earned || 0) + xpEarned);

  if ((existingLogs as DayLog[] | null)?.length) {
    const { error } = await supabase
      .from('day_logs')
      .update({
        status: nextStatus,
        xp_earned: nextXp
      })
      .eq('user_id', userId)
      .eq('date', date);

    if (error) {
      console.error('Error updating day log:', error);
      return false;
    }

    return true;
  }

  const { error } = await supabase
    .from('day_logs')
    .insert({
      user_id: userId,
      date,
      status: nextStatus,
      xp_earned: nextXp
    });

  if (error) {
    console.error('Error inserting day log:', error);
    return false;
  }

  return true;
};

export const replaceDayLog = async (userId: string, date: string, status: DayLog['status'], xpEarned: number): Promise<boolean> => {
  if (status === 'inactive' && xpEarned <= 0) {
    const { error } = await supabase
      .from('day_logs')
      .delete()
      .eq('user_id', userId)
      .eq('date', date);

    if (error) {
      console.error('Error deleting day log during replace:', error);
      return false;
    }

    return true;
  }

  const { data: existingLogs, error: fetchError } = await supabase
    .from('day_logs')
    .select('date')
    .eq('user_id', userId)
    .eq('date', date);

  if (fetchError) {
    console.error('Error fetching day log before replace:', fetchError);
    return false;
  }

  if ((existingLogs as Array<{ date: string }> | null)?.length) {
    const { error } = await supabase
      .from('day_logs')
      .update({
        status,
        xp_earned: Math.max(0, xpEarned)
      })
      .eq('user_id', userId)
      .eq('date', date);

    if (error) {
      console.error('Error replacing existing day log:', error);
      return false;
    }

    return true;
  }

  const { error } = await supabase
    .from('day_logs')
    .insert({
      user_id: userId,
      date,
      status,
      xp_earned: Math.max(0, xpEarned)
    });

  if (error) {
    console.error('Error replacing day log:', error);
    return false;
  }

  return true;
};

export const deleteLatestTaskCompletion = async (userId: string, taskId: string): Promise<TaskCompletion | null> => {
  const { data: latestCompletion, error: fetchError } = await supabase
    .from('task_completions')
    .select('*')
    .eq('user_id', userId)
    .eq('task_id', taskId)
    .order('completed_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (fetchError) {
    console.error('Error fetching latest task completion for deletion:', fetchError);
    return null;
  }

  if (!latestCompletion) {
    return null;
  }

  const { error: deleteError } = await supabase
    .from('task_completions')
    .delete()
    .eq('id', latestCompletion.id);

  if (deleteError) {
    console.error('Error deleting latest task completion:', deleteError);
    return null;
  }

  return latestCompletion as TaskCompletion;
};

// Roulette Ticket operations
export const consumeRouletteTicket = async (userId: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('consume_roulette_ticket', {
    p_user_id: userId
  });

  if (!error) {
    return !!data;
  }

  console.error('Error consuming ticket via RPC:', error);

  // Comentario em pt-BR: fallback direto no perfil quando o RPC nao estiver disponivel ou falhar.
  const profile = await getProfile(userId);
  if (!profile || profile.roulette_tickets <= 0) {
    return false;
  }

  const { error: updateError } = await supabase
    .from('profiles')
    .update({
      roulette_tickets: profile.roulette_tickets - 1
    })
    .eq('id', userId);

  if (updateError) {
    console.error('Error consuming ticket via fallback profile update:', updateError);
    return false;
  }

  return true;
};

export const addRouletteTickets = async (userId: string, amount: number): Promise<boolean> => {
  const { data, error } = await supabase.rpc('add_roulette_tickets', {
    p_user_id: userId,
    p_amount: amount
  });

  if (!error) {
    return !!data;
  }

  console.error('Error adding tickets via RPC:', error);

  if (amount <= 0) {
    return false;
  }

  const profile = await getProfile(userId);
  if (!profile) {
    return false;
  }

  const { error: updateError } = await supabase
    .from('profiles')
    .update({
      roulette_tickets: profile.roulette_tickets + amount
    })
    .eq('id', userId);

  if (updateError) {
    console.error('Error adding tickets via fallback profile update:', updateError);
    return false;
  }

  return true;
};

export const saveRouletteHistory = async (entry: Omit<any, 'id' | 'created_at'>): Promise<boolean> => {
  const { error } = await supabase
    .from('roulette_history')
    .insert(entry);

  if (error) {
    console.error('Error saving roulette history:', error);
    return false;
  }

  return true;
};

export const getRouletteHistory = async (userId: string, limit: number = 10): Promise<any[]> => {
  const { data, error } = await supabase
    .from('roulette_history')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching roulette history:', error);
    return [];
  }

  return data || [];
};

export const checkDailyLoginBonus = async (userId: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('claim_daily_login_bonus', {
    p_user_id: userId
  });

  if (error) {
    console.error('Error checking daily bonus via RPC:', error);
    return false;
  }

  return !!data;
};

export const syncDailyLoginState = async (userId: string): Promise<Profile | null> => {
  const currentProfile = await getProfile(userId);
  if (!currentProfile) {
    return null;
  }

  const bonusAwarded = await checkDailyLoginBonus(userId);
  if (!bonusAwarded) {
    await syncProfileStreakFromDayLogs(userId);
    const profileWithSyncedStreak = await getProfile(userId);
    return profileWithSyncedStreak || currentProfile;
  }

  const today = getCurrentDateOnly();
  const previousLoginDate = currentProfile.last_login_date || null;

  await updateProfile(userId, {
    last_login_date: today
  });

  await syncProfileStreakFromDayLogs(userId);

  const refreshedProfile = await getProfile(userId);
  return refreshedProfile || {
    ...currentProfile,
    last_login_date: today
  };
};
